import type { IncomingMessage } from "http";
import { AreaServer, AuthenticatedGateClient, ServerProcess, ServerAssetManager, SERVER_TYPES, type IServerSystem, type MasterAssetTransport, type ServerGameFile, LEAVE_CHANNEL_REASONS, connectorScopeMatches } from "@gotti/server";
import type { Client as ClientType, IClientSystem as ClientSystemType, AuthenticatedData, JoinedAreaData, JoinedGameInfo, ClientAreaFile, ClientGameFile } from "@gotti/client";
import { GATE_CONNECTOR_REQUEST_PROTOCOLS, GATE_TO_CONNECTOR_PROTOCOLS, LEAVE_AREA_REASONS, PEER_CONNECTION_STATUS, PROTOCOLS, PostConfigData, invokeOrReturn, makeId } from '@gotti/core'
import type { AssetNamespace } from '@gotti/core';
import { GameTimeMeters, createEnvironmentQueries, createProcessResolver } from './environment-lookup.js';
import { createTickQueries } from './environment-ticks.js';
import { createPacingQueries } from './environment-pacing.js';
import { createWaitQueries } from './environment-waits.js';
import { createRenderQueries, watchContextLoss } from './environment-render.js';
import { runClientCreationHooks } from './focus-scoped-input-state.js';
import './bootstrap.js';
import { calculateSplitScreenGrid, applySplitScreenLayout, createCanvasWrapper } from './split-screen.js';

// Local helpers mirroring @gotti/server ServerGameFile exports to avoid deep server import issues during workspace builds
async function makeGameFileDataLocal(gameFile: ServerGameFile<any>, gameId: string | number, configData?: any, skipServer?: boolean): Promise<{ server: any; client: any }> {
  if (typeof (gameFile )?.data === 'function') {
    const { client, server } = await (gameFile ).data({ id: gameId, type: (gameFile ).type, configData });
    return { client: client || {}, server: server || {} };
  } else {
    const sharedGameData = await invokeOrReturn((gameFile )?.data?.shared || {}, { id: gameId, type: (gameFile ).type, configData });
    const client = {
      ...sharedGameData,
      ...(await invokeOrReturn((gameFile )?.data?.client || {}, { id: gameId, type: (gameFile ).type, configData }))
    };
    const server = !skipServer ? {
      ...sharedGameData,
      ...(await invokeOrReturn((gameFile )?.data?.server || {}, { id: gameId, type: (gameFile ).type, configData }))
    } : {};
    return { client, server };
  }
}

async function makeGameFileMetaDataLocal(gameFile: ServerGameFile<any>, id: string | number, configData: any): Promise<any> {
  if (typeof (gameFile )?.meta === 'function') {
    return (gameFile ).meta({ id: id, type: (gameFile ).type, configData });
  } else {
    return (gameFile ).meta;
  }
}

import type { MockClient, MockAreaServer, MockConfigs, MockServerAreaConfig, MockEnvironment, MockEnvironmentOptions, CreateClientsOptions, InitializeClientsConfig, ClientPlaygroundConfigOptions, ClientPlaygroundDomSetupResult, PlaygroundJoinGameOptions } from "./types.js";
import * as BrowserFS from './mocks/nodejs/fs.js';

// Browser-only fs helpers; guard so Node tests won't try to use window/fetch. Asset reads are NOT patched here:
// @gotti/server's AssetReader reads over fetch itself behind its own page opt-in (AssetReader.ASSET_BASE_URL_GLOBAL,
// set by the playground's generated page). A replacement installed on `typeof window` would also run under the CLI's
// node definition pass (it installs a stand-in window), and answering a failed read with `{}` hid missing assets.
if (typeof window !== "undefined") {
  // Also provide a browser fs compatible layer that fetches from assetPath/base
  try {
    // Allow tests/apps to override the fetch base via global
    (window as any).__GOTTI_FS_BASE = (window as any).__GOTTI_FS_BASE || '/';
  } catch {}

  // Ensure require('fs') in browser resolves to our fetch-backed mock
  try {
    const prevRequire = (window as any).require;
    (window as any).require = (id: string) => {
      if (id === 'fs' || id === 'node:fs') return BrowserFS;
      return typeof prevRequire === 'function' ? prevRequire(id) : {};
    };
  } catch {}
}

const jsonCopy = <T>(v: T): T => JSON.parse(JSON.stringify(v));

/**
 * The area servers and clients of torn-down environments (`teardownEnv`). The mock transport moves nothing from or to
 * them: a message queued before the teardown, or sent after it by the env's leftover code (a system's own timer, a
 * pending promise), is dropped where it would have been delivered. The patched prototypes serve every env, the next
 * one included, so it is the ENDPOINTS that are closed, not a per-env flag a later env's patches would not read.
 */
const closedEndpoints = new WeakSet<object>();

/**
 * The peer requests each client has in flight — sent, the peer's system still deciding — by the client it asked.
 * Production's requester holds a pending connection from the moment it sends a request, and its peer holds one for the
 * requester while its system decides, so neither can send the other a request then (`addPeer`).
 */
const peerRequestsInFlight = new WeakMap<object, Set<object>>();
const peerRequestInFlight = (from: object, to: object): boolean => peerRequestsInFlight.get(from)?.has(to) === true;
const isClosedEndpoint = (endpoint: unknown): boolean =>
  typeof endpoint === 'object' && endpoint !== null && closedEndpoints.has(endpoint);
/** `deliver`, unless an end of it belongs to a torn-down environment (see `closedEndpoints`). */
const unlessClosed = <T>(from: unknown, to: unknown, deliver: () => T) => (): T | undefined =>
  isClosedEndpoint(from) || isClosedEndpoint(to) ? undefined : deliver();

/**
 * Deliver a peer message to a receiving client, routing it the same way the
 * real WebRTC connector does. The in-memory mock used by the sandbox /
 * playground has no connector, so peer dispatch hands off here to keep
 * virtual P2P behaviour identical to production.
 *
 * Mirrors `BaseClientConnector`'s peer `onMessage` handler
 * (packages/client/src/ClientConnectors/BaseClientConnector.ts):
 *   - a message carrying a `to` field is routed to that system's
 *     `onPeerSystemMessage(senderId, message)` (the full envelope, `to`
 *     included, is delivered verbatim);
 *   - a message without `to` falls through to the client-level
 *     `handlePeerMessage` catch-all.
 */
export const deliverPeerMessageToClient = (
  targetClient: MockClient | undefined,
  senderId: string | number,
  message: any,
): void => {
  if (!targetClient || isClosedEndpoint(targetClient)) return;
  if (message?.to !== undefined) {
    const system = (targetClient as any).process?.systemLookup?.[message.to];
    system?.onPeerSystemMessage?.(senderId, message);
    return;
  }
  targetClient.handlePeerMessage?.(senderId, message);
};

/**
 * Fire the peer-connection "started" hooks on both ends of a freshly
 * established virtual link, matching production (BaseClientConnector.onConnected,
 * asserted by apps/integration-tests/.../ClientToClient.test.ts):
 *   - the INITIATOR's systems receive the responder's acceptance value
 *     (whatever its onPeerSystemConnectionRequest returned);
 *   - the RESPONDER's systems receive the initiator's original request options
 *     (what was passed to addPeer).
 * On each side, every system gets onAnyPeerConnectionStarted and the owning
 * system additionally gets onPeerSystemConnectionStarted.
 */
export const fireVirtualPeerConnectionStarted = (args: {
  initiator: MockClient;
  initiatorSeesPeerAs: string | number;
  responder: MockClient;
  responderSeesPeerAs: string | number;
  systemName: string | number;
  requestOptions: any;
  acceptValue: any;
}): void => {
  const { initiator, initiatorSeesPeerAs, responder, responderSeesPeerAs, systemName, requestOptions, acceptValue } = args;
  const trigger = (c: MockClient, peerId: string | number, opts: any) => {
    (c as any).process?.systemArray?.forEach((s: any) => s.onAnyPeerConnectionStarted?.(peerId, opts));
    (c as any).process?.systemLookup?.[systemName]?.onPeerSystemConnectionStarted?.(peerId, opts);
  };
  trigger(initiator, initiatorSeesPeerAs, acceptValue);     // initiator ← responder's acceptance value
  trigger(responder, responderSeesPeerAs, requestOptions);  // responder ← initiator's request options
};

/**
 * The connector-identity layer for the mock env, built once per `initializer()`
 * call in its closure and threaded onto the client-prototype state so the
 * (module-global) patched `joinGame`/`rejoinGame` can reach it. Models
 * per-client connector assignment, connector death, and the reconnect
 * re-selection the real gate would do.
 */
interface ConnectorRuntime {
  /** Every configured connector id (dead + live). */
  ids: string[];
  /** Connectors that have been `killConnector`'d. */
  dead: Set<string>;
  /**
   * Assign a connector to a NEW client. `explicit` (connectorId) is a raw force;
   * `prefer` (preferConnector) is scope-checked against the join target `areas`
   * and THROWS when out of scope; otherwise the least-loaded live in-scope
   * connector is chosen. Mirrors the real gate's scope + least-loaded selection.
   */
  assignInitial: (opts?: { explicit?: string; prefer?: string; areas?: (string | number)[] }) => string;
  /**
   * The least-loaded LIVE (not dead, not draining) connector whose scope covers
   * every area in `areas` — what the gate re-picks on reconnect. `undefined` if
   * none qualify. With no `areas`, scope is not consulted (any live connector).
   */
  leastLoadedLive: (areas?: (string | number)[]) => string | undefined;
  /** Record (and stamp onto the client) a client's current connector. */
  register: (client: MockClient, connectorId: string) => void;
  /** True when this client's current connector is dead (dispatch must be skipped). */
  isDeadFor: (client: MockClient | undefined) => boolean;
  /** Build the in-process connector object handed to `client.process.initialize`.
   *  `previousConnectorId` is passed only when this connector is being built for
   *  a REJOIN (it rides on the re-reserved seat, like the real gate's). */
  makeConnector: (client: MockClient, connectorId: string, previousConnectorId?: string) => any;
}

interface PlaygroundClientPrototypeState {
  gate?: MockEnvironmentOptions['gate'];
  clients: { [key: string]: MockClient };
  mockHttpReq: IncomingMessage;
  clientGameData: any;
  clientGames: ClientGameFile[];
  serverGameType: string;
  gameId: string | number;
  areas: { [areaId: string]: MockAreaServer };
  config?: MockConfigs;
  master?: MockEnvironmentOptions['master'];
  connectorRuntime?: ConnectorRuntime;
}

let currentClientPrototypeState: PlaygroundClientPrototypeState | null = null;
let clientPrototypeOverridesApplied = false;
let originalClientAuthenticate: ((this: ClientType, ...args: any[]) => any) | undefined;
let originalClientJoinGame: ((this: ClientType, ...args: any[]) => any) | undefined;
let originalClientRejoinGame: ((this: ClientType, ...args: any[]) => any) | undefined;
let originalClientJoinArea: ((this: ClientType, ...args: any[]) => any) | undefined;
let originalClientJoinInitialAreas: ((this: ClientType, ...args: any[]) => any) | undefined;
let originalClientSendAllPeers: ((this: ClientType, ...args: any[]) => any) | undefined;
let originalClientSendPeer: ((this: ClientType, ...args: any[]) => any) | undefined;
let originalClientSendPeers: ((this: ClientType, ...args: any[]) => any) | undefined;

/**
 * Records `payload` as what `client` authenticated with — `MockClient.authPayload`, read-only to everyone
 * else. Written where the mock authenticates, so a client has it whichever path created it.
 */
const recordAuthPayload = (client: MockClient, payload: unknown): void => {
  (client as { authPayload?: unknown }).authPayload = payload;
};

const authenticateWithPlayground = async (
  client: MockClient,
  data: any,
  state: PlaygroundClientPrototypeState
): Promise<AuthenticatedData> => {
  if (!state.gate) {
    const id = data?.id ?? `${MOCK_CLIENT_ID}_${last_client_index++}`;
    const auth: AuthenticatedData = { publicId: id, privateId: id, auth: data };
    client.privateId = id;
    client.publicId = id;
    client.auth.privateId = auth.privateId;
    client.auth.publicId = auth.publicId;
    client.auth.id = auth.publicId;
    client.auth.data = auth.auth;
    recordAuthPayload(client, data);
    state.clients[id] = client;
    if (client.auth.publicId != null) {
      state.clients[String(client.auth.publicId)] = client;
    }
    return auth;
  }

  const auth: AuthenticatedGateClient = await state.gate.checkAuth(data, { ...(state.mockHttpReq as any) });
  // The payload as sent, not what the gate's onAuthenticate made of it (that is `auth.data` below).
  recordAuthPayload(client, data);
  const { publicId, privateId, auth: authData } = auth;
  client.privateId = privateId;
  client.publicId = publicId as string;
  client.auth.privateId = auth.privateId;
  client.auth.id = auth.publicId as string | number;
  client.auth.publicId = auth.publicId as string;
  client.auth.data = authData;
  state.clients[String(privateId)] = client;
  if (publicId != null) {
    state.clients[String(publicId)] = client;
  }
  return { publicId, privateId, auth: authData } as AuthenticatedData;
};

const joinGameWithPlayground = async (
  client: MockClient,
  options: any,
  initialAreaOptions: any,
  suppressJoinAreas: boolean | undefined,
  state: PlaygroundClientPrototypeState
) => {
  let joinInfo: { game: JoinedGameInfo; joiningAreaIds: string[] } | null = null;
  if (state.gate) {
    const info: JoinedGameInfo = await state.gate.handleJoinGame(client.privateId, options, state.mockHttpReq);
    // Parity with the REAL Client.joinGame, which adopts the publicId the gate
    // reserved the seat under — it may be a RESUMED session's, not the one
    // checkAuth minted, and it is what the connector validates the connection
    // against. The gate above is the REAL one (the initializer no longer
    // overrides handleJoinGame/reserveSeat, only the connector transport under
    // them), so this adoption is the actual identity-resume step, not a no-op.
    if (info.publicId !== undefined && info.publicId !== null) {
      const previousPublicId = client.publicId;
      client.publicId = info.publicId as string;
      client.auth.publicId = info.publicId as string;
      client.auth.id = info.publicId;
      // Drop the abandoned publicId key, but never the privateId key that
      // `authenticateWithPlayground` also registered this client under.
      if (previousPublicId != null
        && String(previousPublicId) !== String(info.publicId)
        && String(previousPublicId) !== String(client.privateId)) {
        delete state.clients[String(previousPublicId)];
      }
      state.clients[String(info.publicId)] = client;
    }
    joinInfo = { game: info, joiningAreaIds: [] };
  } else {
    // No gate: stand in for a pass-through one. The real gate's reserveSeat makes
    // the raw join body the SEAT's options (client-asserted `rejoin` /
    // `rejoinAreaIds` stripped — both are server-derived), and every first join
    // carries the seat's options to the area, so `onJoinRequest` and the join
    // hooks see the body with or without a gate. Recorded where the gated path's
    // RESERVE_SEAT stub records it; a body that is not an object (a bare gameId)
    // carries no options, exactly as before.
    if (options && typeof options === 'object' && !Array.isArray(options)) {
      const { rejoin: _rejoin, rejoinAreaIds: _rejoinAreaIds, ...seatOptions } = options;
      client.reservedSeatOptions = seatOptions;
    } else {
      client.reservedSeatOptions = undefined;
    }
    joinInfo = {
      game: {
        gameData: state.clientGameData,
        gameId: state.gameId,
        gameType: state.serverGameType,
        host: "",
        port: 0,
        connectorType: "websocket",
      } as JoinedGameInfo,
      joiningAreaIds: [],
    };
  }

  // Give the client a REAL (in-process) connector bound to its assigned
  // connector id, replacing the old inert `{}`. This is what lets the client's
  // real `handleConnectionLost` reconnect driver run in the mock: `killConnector`
  // fires `client.connector.onConnectionLost()`. `wireConnectionLost` (the real
  // Client seam) points that callback at `handleConnectionLost`, exactly as
  // production does after every connector construction. When no connector
  // runtime exists (should not happen — always defaulted), fall back to the
  // historical inert object so behaviour is byte-identical.
  const rt = state.connectorRuntime;
  const connectorId = (client as any).connectorId ?? rt?.assignInitial() ?? 'mock_connector';
  if (rt) rt.register(client, connectorId);
  const connector = rt ? rt.makeConnector(client, connectorId) : ({} as any);
  (client as any).connector = connector;
  (client as any).wireConnectionLost?.();
  client.process.initialize(connector);
  const cg = state.clientGames.find((g: any) => g.type === joinInfo.game.gameType);
  if (!cg) throw new Error(`No game found for type ${joinInfo.game.gameType}`);
  await client.startGame(cg, joinInfo.game.gameData);
  if (!suppressJoinAreas && Array.isArray(initialAreaOptions)) {
    joinInfo.joiningAreaIds = await client.joinInitialAreas(initialAreaOptions);
  }
  client.emit("joined-game");
  return joinInfo;
};

/**
 * Mock analogue of `Client.rejoinGame` (the private helper the real
 * `handleConnectionLost` reconnect driver calls). The real one does a gate
 * `httpPostAsync` + builds a WebSocket connector — neither works in-process —
 * so we patch THIS seam (exactly as the mock already replaces `joinGame` with
 * `joinGameWithPlayground`), NOT the driver: `handleConnectionLost` itself (the
 * backoff loop, attempt cap, `connection-lost`/`reconnecting`/`reconnected`/
 * `reconnect-failed` events and per-area `onReconnect*` hooks) runs REAL and
 * unmodified. Here we only stand in for the gate: pick the least-loaded LIVE
 * connector (the gate's reconnect re-selection), build a fresh in-process
 * connector bound to it, re-`initialize` the process, and re-run
 * `connector.pingForFirstAreas` — which (like the real server, from the areas it
 * holds) drives the REAL `AreaServer.handleTryJoinArea` rebind path. Throwing
 * (no live connector) is how a failed attempt feeds the real backoff loop.
 */
const rejoinGameWithPlayground = async (
  client: MockClient,
  state: PlaygroundClientPrototypeState,
) => {
  const rt = state.connectorRuntime;
  if (!rt) throw new Error('[test-utils] rejoinGame: no connector runtime');
  // The dead connector was already disconnected+dropped by handleConnectionLost;
  // clear any straggler from a previous failed attempt in this same episode.
  (client as any).connector?.disconnect?.();
  // Scope-aware reconnect target: the surviving connector must cover this
  // client's still-joined areas (mirrors the real gate re-selection). A draining
  // connector is excluded, so a graceful removeConnector migrates cleanly.
  const newConnectorId = rt.leastLoadedLive(Object.keys(client.process.joinedAreasById));
  if (newConnectorId == null) {
    // No surviving connector to seat on — feed the real backoff/attempt loop.
    throw new Error('[test-utils] reconnect failed: no live connector available');
  }
  // The connector the client was on when it dropped — the gate puts this on the
  // re-reserved seat as `previousConnectorId`, so the areas see it too.
  const previousConnectorId = (client as any).connectorId as string | undefined;
  // A reconnect re-posts the SAME game-join body to the gate (real rejoinGame's
  // httpPostAsync), which is what re-reserves the seat: the gate re-derives the
  // held areas, re-stamps `rejoin`/`previousConnectorId` onto the seat options,
  // and cancels the grace hold. Only reachable when this env was given a gate;
  // a throw here feeds the real backoff loop, exactly as a gate error does.
  if (state.gate) {
    await state.gate.handleJoinGame(client.privateId, (client as any).lastGameJoinBody, state.mockHttpReq);
  }
  const connector = rt.makeConnector(client, newConnectorId, previousConnectorId);
  (client as any).connector = connector;
  rt.register(client, newConnectorId);      // dispatch resumes; internal maps track c2
  (client as any).wireConnectionLost?.();   // real seam: point onConnectionLost at handleConnectionLost
  client.process.initialize(connector);
  // Mirrors the real rejoinGame: NO client-asserted area list. Route the rebind
  // through the connector (real rejoinGame → this.connector.pingForFirstAreas({ ... })).
  await connector.pingForFirstAreas({});
};

const joinAreaWithPlayground = async (
  client: MockClient,
  areaId: string,
  options: any,
  state: PlaygroundClientPrototypeState
): Promise<{ area: JoinedAreaData; options: any }> => {
  const area = state.areas[areaId];
  if (!area) throw new Error(`Area ${areaId} not found`);
  let joined: any = null;
  let joinError: any = null;
  return new Promise((resolve, reject) => {
    orderedSetTimeout(client.auth.privateId as string, areaId, unlessClosed(client, area, async () => {
      try {
        joined = await area.handleTryJoinArea(client.id as number, client.auth.data, (client as any).connectorId ?? 'mock_connector', options);
      } catch (err) {
        joinError = err;
      }
      orderedSetTimeout(areaId, client.auth.privateId as string, unlessClosed(area, client, async () => {
        if (joined) {
          return resolve({
            area: {
              id: areaId,
              type: area.areaFile?.type,
              data: area.areaClientData,
              state: area.areaState.client,
            },
            options,
          });
        }
        return reject(joinError || new Error(`Failed to join area ${areaId}`));
      }), getNumber(state.config?.clientServerLatency, true));
    }), getNumber(state.config?.clientServerLatency));
  });
};

const joinInitialAreasWithPlayground = async (
  client: MockClient,
  areasToJoin: string[],
  state: PlaygroundClientPrototypeState
): Promise<string[]> => {
  const areaIds: string[] = [];
  if (state.master) {
    // Placeholder for future master logic
  }
  areaIds.push(...areasToJoin);

  const buildJoinOptions = (areaId: string): Record<string, any> => {
    const reserved = client.reservedSeatOptions;
    const authData = (client.auth?.data ?? {}) as Record<string, any>;
    const baseOptions = reserved && typeof reserved === 'object' && !Array.isArray(reserved) ? { ...reserved } : {};

    // Merge auth data and reserved seat options - no hardcoded defaults here.
    // Any demo-specific defaults (nickname, color, position, etc.) should be
    // set by the playground config's clientAuth or in the area's onClientJoined handler.
    return {
      id: client.id,
      areaId,
      ...authData,
      ...baseOptions,
    };
  };

  areaIds.forEach((aId) => {
    const joinOptions = buildJoinOptions(aId);
    const area = state.areas[aId];
    if (!area) {
      throw new Error(`Area ${aId} not found`);
    }
    // Delivered the way the real connector delivers a first join
    // (ConnectorServer.handleGetFirstAreas → PROTOCOLS.CLIENT_JOINED_AREA), which
    // AreaServer.onServerMessage hands to handleReconnectTransportJoin with
    // `void`: the area's `onJoinRequest` decides a first join (fail-open when it
    // throws) and the client is seated with what it returns; an area with no
    // `onJoinRequest` seats it synchronously, right here, with `joinOptions`.
    // Calling handleClientJoin directly skipped `onJoinRequest` altogether, so an
    // admission hook tested through the mock passed whatever it did.
    area.onServerMessage(
      { id: (client as any).connectorId ?? "mock_connector", type: SERVER_TYPES.CONNECTOR } as any,
      [PROTOCOLS.CLIENT_JOINED_AREA, client.id, client.auth.data, joinOptions],
    );
  });
  return areaIds;
};

const sendAllPeersWithPlayground = (client: MockClient, message: any) => {
  if (isClosedEndpoint(client)) return;
  Object.values(client.peerLookup).forEach((p: any) => {
    deliverPeerMessageToClient(p.client, client.id, jsonCopy(message));
  });
};

const sendPeerWithPlayground = (client: MockClient, peerId: string | number, state: PlaygroundClientPrototypeState, message: any) => {
  if (isClosedEndpoint(client)) return;
  const target = Object.values(state.clients).find((c: any) => (c as MockClient).id === peerId);
  deliverPeerMessageToClient(target as MockClient | undefined, client.id, jsonCopy(message));
};

const sendPeersWithPlayground = (client: MockClient, peerIds: Array<string | number>, state: PlaygroundClientPrototypeState, message: any) => {
  if (isClosedEndpoint(client)) return;
  const targets = Object.values(state.clients).filter((c: any) => peerIds.includes((c as MockClient).id));
  targets.forEach((target: any) => {
    deliverPeerMessageToClient(target, client.id, jsonCopy(message));
  });
};

const ensureClientPrototypeOverrides = (ClientCtor: typeof ClientType) => {
  if (clientPrototypeOverridesApplied) {
    return;
  }
  const proto = ClientCtor.prototype as any;
  originalClientAuthenticate = typeof proto.authenticate === 'function' ? proto.authenticate : undefined;
  originalClientJoinGame = typeof proto.joinGame === 'function' ? proto.joinGame : undefined;
  originalClientRejoinGame = typeof proto.rejoinGame === 'function' ? proto.rejoinGame : undefined;
  originalClientJoinArea = typeof proto.joinArea === 'function' ? proto.joinArea : undefined;
  originalClientJoinInitialAreas = typeof proto.joinInitialAreas === 'function' ? proto.joinInitialAreas : undefined;
  originalClientSendAllPeers = typeof proto.sendAllPeers === 'function' ? proto.sendAllPeers : undefined;
  originalClientSendPeer = typeof proto.sendPeer === 'function' ? proto.sendPeer : undefined;
  originalClientSendPeers = typeof proto.sendPeers === 'function' ? proto.sendPeers : undefined;

  proto.authenticate = async function (...args: any[]) {
    if (currentClientPrototypeState) {
      return authenticateWithPlayground(this as unknown as MockClient, args[0], currentClientPrototypeState);
    }
    if (originalClientAuthenticate) {
      return originalClientAuthenticate.apply(this, args);
    }
    return undefined;
  };

  proto.joinGame = async function (...args: any[]) {
    if (currentClientPrototypeState) {
      const [options, initialAreas, suppressJoinAreas] = args;
      return joinGameWithPlayground(
        this as unknown as MockClient,
        options,
        initialAreas,
        suppressJoinAreas,
        currentClientPrototypeState
      );
    }
    if (originalClientJoinGame) {
      return originalClientJoinGame.apply(this, args);
    }
    return undefined;
  };

  // Patch the private reconnect gate-flow seam. handleConnectionLost (the real
  // driver) calls this.rejoinGame(); in the mock it must NOT hit the real
  // httpPostAsync/WebSocket path — see rejoinGameWithPlayground.
  proto.rejoinGame = async function (...args: any[]) {
    if (currentClientPrototypeState) {
      return rejoinGameWithPlayground(this as unknown as MockClient, currentClientPrototypeState);
    }
    if (originalClientRejoinGame) {
      return originalClientRejoinGame.apply(this, args);
    }
    return undefined;
  };

  proto.joinArea = async function (...args: any[]) {
    if (currentClientPrototypeState) {
      return joinAreaWithPlayground(
        this as unknown as MockClient,
        args[0],
        args[1],
        currentClientPrototypeState
      );
    }
    if (originalClientJoinArea) {
      return originalClientJoinArea.apply(this, args);
    }
    return undefined;
  };

  proto.joinInitialAreas = async function (...args: any[]) {
    if (currentClientPrototypeState) {
      return joinInitialAreasWithPlayground(
        this as unknown as MockClient,
        args[0],
        currentClientPrototypeState
      );
    }
    if (originalClientJoinInitialAreas) {
      return originalClientJoinInitialAreas.apply(this, args);
    }
    return undefined;
  };

  proto.sendAllPeers = function (...args: any[]) {
    if (currentClientPrototypeState) {
      return sendAllPeersWithPlayground(this as unknown as MockClient, args[0]);
    }
    if (originalClientSendAllPeers) {
      return originalClientSendAllPeers.apply(this, args);
    }
    return undefined;
  };

  proto.sendPeer = function (...args: any[]) {
    if (currentClientPrototypeState) {
      return sendPeerWithPlayground(
        this as unknown as MockClient,
        args[0],
        currentClientPrototypeState,
        args[1]
      );
    }
    if (originalClientSendPeer) {
      return originalClientSendPeer.apply(this, args);
    }
    return undefined;
  };

  proto.sendPeers = function (...args: any[]) {
    if (currentClientPrototypeState) {
      return sendPeersWithPlayground(
        this as unknown as MockClient,
        args[0],
        currentClientPrototypeState,
        args[1]
      );
    }
    if (originalClientSendPeers) {
      return originalClientSendPeers.apply(this, args);
    }
    return undefined;
  };

  clientPrototypeOverridesApplied = true;
  try {
    (globalThis as any).__GOTTI_PLAYGROUND_PATCHED = true;
  } catch {
    // ignore if globalThis not available
  }
};

const setClientPrototypeState = (state: PlaygroundClientPrototypeState) => {
  currentClientPrototypeState = state;
};

export const MOCK_GAME_ID = "mock_game_id";
export const MOCK_CLIENT_ID = "mock_client_id";

let last_client_index = 0;

// Ordered message queues for deterministic delivery between endpoints
type PendingMsg = { sequence: number; callback: Function; timestamp: number; readyTime: number; args: any[] };
const orderedMessageQueues: Record<string, { nextSequence: number; lastProcessed: number; pendingMessages: PendingMsg[] }> = {};

/**
 * Drop ALL pending transport messages + reset every channel's sequence counters.
 *
 * The `orderedMessageQueues` are MODULE-GLOBAL and keyed by `${sender}->${receiver}`
 * (e.g. `sandbox-area-1->player-1`), so TWO concurrent runtimes that use the same
 * area/client ids (the live sandbox preview on the real clock + a test runtime on
 * the virtual clock) share — and corrupt — the same channels: the preview's
 * real-clock messages (readyTime ≈ 1.78e12) sit at low sequence numbers and, under
 * the virtual clock (now ≈ 200), can NEVER become ready, head-of-line-blocking the
 * test's own join/attach messages forever. Calling this between runtimes (e.g. when
 * a test run takes over from the live preview, and at the start of each test boot)
 * gives the incoming runtime clean channels so its messages aren't stuck behind a
 * different clock's stale traffic.
 */
export function resetOrderedMessageQueues(): void {
  for (const k of Object.keys(orderedMessageQueues)) delete orderedMessageQueues[k];
}

export function orderedSetTimeout(sender: string | number, receiver: string | number, callback: Function, delay: number, ...args: any[]) {
  const channelId = `${sender}->${receiver}`;
  if (!orderedMessageQueues[channelId]) {
    orderedMessageQueues[channelId] = { nextSequence: 0, lastProcessed: -1, pendingMessages: [] };
  }
  const queue = orderedMessageQueues[channelId];
  const sequence = queue.nextSequence++;
  const timestamp = Date.now();
  const readyTime = timestamp + delay;
  queue.pendingMessages.push({ sequence, callback, timestamp, readyTime, args });
  setTimeout(() => processOrderedQueue(channelId), delay);
  return -1;
}

function processOrderedQueue(channelId: string): void {
  const queue = orderedMessageQueues[channelId];
  if (!queue) return;
  const now = Date.now();
  let progressed = true;
  while (progressed) {
    progressed = false;
    const next = queue.pendingMessages.find((m) => m.sequence === queue.lastProcessed + 1 && m.readyTime <= now);
    if (next) {
      queue.pendingMessages = queue.pendingMessages.filter((m) => m !== next);
      queue.lastProcessed = next.sequence;
      // Fire and forget - don't await, just like real setTimeout.
      // Isolate per message: a throwing handler (e.g. a game system crashing on
      // one onServerMessage) must not escape into the timer task or abort the
      // drain of ready successors — the real transport delivers each message as
      // its own event, so one bad handler never blocks the next message.
      try {
        next.callback(...next.args);
      } catch (err) {
        console.error(`[test-utils] delivery callback threw on channel ${channelId} — continuing drain`, err);
      }
      progressed = true;
    }
  }
  // The next message in order is not due yet: come back when it is. Not only when a later message is due and waits
  // behind it — its own timer can fire up to 1 ms before Date.now() reaches its readyTime: Node times the timer on
  // libuv's monotonic clock, truncated to whole ms, while readyTime is read off Date.now() — the wall clock, truncated
  // on its own — so a timer due on the one can find the other 1 ms short (seen: 1 ms early). With nothing else on the
  // channel no other timer would ever come back for it: it stayed queued for good.
  const head = queue.pendingMessages.find((m) => m.sequence === queue.lastProcessed + 1);
  if (head && head.readyTime > now) {
    setTimeout(() => processOrderedQueue(channelId), Math.max(1, head.readyTime - now));
  }
}

function flushReadyMessages(channelId: string): void {
  const queue = orderedMessageQueues[channelId];
  if (!queue) return;
  const now = Date.now();
  const ready = queue.pendingMessages.filter((m) => m.readyTime <= now);
  if (ready.length > 0) processOrderedQueue(channelId);
}

if (typeof window !== "undefined") {
  setInterval(() => { Object.keys(orderedMessageQueues).forEach(flushReadyMessages); }, 1000 / 30);
}

const getNumber = (v?: number | { min: number; max: number }, useMax = false): number => {
  if (!v) return 0;
  if (typeof v === "number") return v;
  const { min, max } = v;
  return useMax ? max : Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Resolve the simulated one-way latency (ms) to apply to a client's peer
 * dispatch — the P2P analogue of the server-ping resolution
 * (`client.simulatedLatency ?? config.clientServerLatency`).
 *
 * Fallback chain: the explicit per-client peer override
 * (`simulatedPeerLatency`) → the client's server ping (`simulatedLatency`,
 * i.e. peer latency INHERITS server ping when unset) → the global
 * `config.clientPeerLatency`. An explicit `0` is honored (it is not treated as
 * "unset", so it short-circuits the inherit chain).
 */
export const effectivePeerLatency = (
  client: { simulatedPeerLatency?: number; simulatedLatency?: number } | undefined,
  config?: { clientPeerLatency?: number | { min: number; max: number } },
): number => {
  const raw = client?.simulatedPeerLatency ?? client?.simulatedLatency ?? config?.clientPeerLatency;
  return getNumber(raw);
};

/**
 * Mirror of `Starter.validateReconnectConfig` (packages/deployer) — the boot-time
 * contract for an area that HOLDS seats. A rejoin is matched on the returning
 * client's server-verified auth identity, so an area that enables reconnect
 * without a game file saying where that identity lives would hold seats nobody
 * can ever claim. The real Starter fails the boot for it; the mock env must too,
 * or the playground silently accepts a topology a deploy would reject.
 */
// The Starter's sibling `globalSingleton` check is deliberately NOT mirrored: it
// exists because a real globalSingleton area is booted with no parent game file,
// and every area in this env is booted with `serverGame.gameFile`, so the branch
// could never be reached here. The authIdentityPath check below is the one that
// can actually fire.
const validateMockReconnectConfig = (areaFile: any, gameFile: any, areaId: string | number) => {
  const policy = areaFile?.reconnect ?? gameFile?.reconnect;
  if (!policy?.enabled || !(policy.graceMs > 0)) return;
  if (!gameFile?.authIdentityPath) {
    throw new Error(
      `[test-utils] Area server file: ${areaFile.type} (area: ${areaId}) enables reconnect, but authIdentityPath is not declared ` +
      `on its parent game file ${gameFile?.type ?? '(unknown)'}. An automatic rejoin is matched on the ` +
      `server-verified auth identity, so the game must say where that identity lives ` +
      `(e.g. authIdentityPath: 'attributes.accountId').`
    );
  }
};

export const initializer = async (
  opts: MockEnvironmentOptions,
  config?: MockConfigs
): Promise<MockEnvironment> => {
  const { clientGame, serverGame, clientArea, serverArea, gate, master } = opts;
  const mockHttpReq: IncomingMessage = {
    aborted: false,
    httpVersion: "",
    httpVersionMajor: 0,
    httpVersionMinor: 0,
    complete: false,
    connection: {} as any,
    socket: {} as any,
    headers: {},
    headersDistinct: {} ,
    rawHeaders: [],
    trailers: {},
    trailersDistinct: {} ,
    rawTrailers: [],
    setTimeout() { throw new Error("not implemented"); },
    destroy() { throw new Error("not implemented"); },
    readableAborted: false,
    readable: false,
    readableDidRead: false,
    readableEncoding: null,
    readableEnded: false,
    readableFlowing: null,
    readableHighWaterMark: 0,
    readableLength: 0,
    readableObjectMode: false,
    destroyed: false,
    closed: false,
    errored: null,
  } as unknown as IncomingMessage;

  // ── Asset seam ──────────────────────────────────────────────────────────
  // ONE ServerAssetManager (shared by every server AND client system this
  // boot creates) over an in-memory "master": writes land in a Map and fire
  // applyRemoteChange (so onAssetUpdated/onAssetRemoved fire like production,
  // where the master's fan-out does this); cold reads check the Map, then
  // fall back to the caller-supplied AssetSource (defaults to nothing, so the
  // whole seam is a no-op when `opts.assets` is omitted).
  const assetStore = new Map<string, { bytes: Uint8Array; mimeType: string }>();
  let assetManager!: ServerAssetManager;
  const masterAssetTransport: MasterAssetTransport = {
    async write(op: 'save' | 'remove', { namespace, path, bytes }: { namespace: AssetNamespace; path: string; bytes?: Uint8Array }) {
      const key = `${namespace}/${path}`;
      if (op === 'save') {
        assetStore.set(key, {
          bytes: bytes ?? new Uint8Array(),
          // `write` isn't given a mimeType (mirrors the real MasterAssetTransport
          // shape) — keep whatever this key already had, or a sane default.
          mimeType: assetStore.get(key)?.mimeType ?? 'application/octet-stream',
        });
      } else {
        assetStore.delete(key);
      }
      await assetManager.applyRemoteChange(op === 'save' ? 'updated' : 'removed', {
        namespace,
        path,
        byteSize: bytes?.byteLength ?? 0,
        changedBy: { type: 'area', areaId: 'mock' },
      });
    },
    async read({ namespace, path }: { namespace: AssetNamespace; path: string }) {
      const key = `${namespace}/${path}`;
      const entry = assetStore.get(key);
      if (entry) return { bytes: entry.bytes, mimeType: entry.mimeType };
      const loaded = await opts.assets?.source?.load(key);
      // Pass the source's decoded forms (image/url) through the transport read
      // contract so a sync getImage()/getUrl() after preload works — a DOM-backed
      // source (the sandbox's browser source) decodes images here.
      return loaded
        ? { bytes: loaded.bytes, mimeType: loaded.mimeType, image: loaded.image, url: loaded.url }
        : null;
    },
  };
  assetManager = new ServerAssetManager(masterAssetTransport);

  // Both ServerProcess and ClientProcess lazily build (and memoize on `this.
  // assetManager`) their OWN manager the first time any system is added
  // (ServerProcess.ensureAssetManager / ClientProcess.ensureAssetManager),
  // which is also the exact call site that attaches `$assets` to that system
  // (onAddedSystem → attachServerAssets/attachClientAssets) and the one
  // `startGame` uses to resolve a game/area file's declared `preloads` before
  // any system's onInit runs. There is no seam to call attachServerAssets/
  // attachClientAssets ourselves BETWEEN system construction and onInit (both
  // happen synchronously, back-to-back, deep inside AreaServer.initialize()/
  // ClientProcess.startGame() — see onAddedSystem in ServerProcess.ts /
  // ClientProcess.ts), so instead we make that memoized field resolve to OUR
  // shared manager: every system this env boots (server AND client) ends up
  // wired to the same in-memory transport, with zero duplicate-attach risk
  // and file-declared `preloads` resolved for free through the same production
  // code path (identical "Asset preload failed" error wrapping).
  // Reassigned fresh per initializer() call (mirrors the AreaServer.prototype
  // dispatch patches above/below — this env's manager is what this env's
  // processes should see).
  (ServerProcess.prototype as any).ensureAssetManager = function (this: any): ServerAssetManager {
    if (!this.assetManager) this.assetManager = assetManager;
    return this.assetManager;
  };

  // Union member of the preload set that ISN'T attached to any game/area file
  // (opts.assets.preload) — resolve it ourselves, before any process starts,
  // so it's already cache-warm by the time file-declared preloads (handled by
  // the production preloadAssets() call above) and onInit run.
  if (opts.assets?.preload?.length) {
    await Promise.all(opts.assets.preload.map(async (key) => {
      try {
        await assetManager.loadRaw(key);
      } catch (e) {
        throw new Error(`Asset preload failed for "${key}": ${e instanceof Error ? e.message : String(e)}`);
      }
    }));
  }

  const clientGames: ClientGameFile[] = Array.isArray(clientGame) ? clientGame : [clientGame];
  const GAME_ID = serverGame.id ?? MOCK_GAME_ID;

  const meta = await makeGameFileMetaDataLocal(
    serverGame.gameFile as ServerGameFile,
    GAME_ID,
    serverGame.configData
  );

  const gameData: PostConfigData = { id: GAME_ID, meta, type: (serverGame.gameFile ).type, configData: serverGame.configData } ;

  const { client: clientGameData } = await makeGameFileDataLocal(
    serverGame.gameFile,
    GAME_ID,
    config?.gameConfigData,
    false
  );

  const clientAreaFiles: ClientAreaFile[] = Array.isArray(clientArea) ? clientArea : [clientArea];
  const serverAreas = Array.isArray(serverArea) ? serverArea : [serverArea];
  // Dynamic-area TEMPLATES (`dynamic: true`): registered by areaFile.type for
  // the patched createArea below, never booted at init — mirrors the Starter,
  // which skips dynamic entries in the static boot and registers them as types.
  const staticServerAreas = serverAreas.filter((a) => !a?.dynamic);
  const dynamicAreaTemplates = new Map<string, MockServerAreaConfig>();
  serverAreas.filter((a) => a?.dynamic).forEach((tpl) => {
    const templateType = tpl.areaFile?.type as string;
    if (!templateType) throw new Error('[test-utils] a dynamic serverArea template requires an areaFile with a type');
    if (!dynamicAreaTemplates.has(templateType)) dynamicAreaTemplates.set(templateType, tpl);
  });

  const areaServers: { [areaId: string]: MockAreaServer } = {};
  const areas = areaServers;
  const clients: { [key: string]: MockClient } = {};
  // Every client THIS env constructed, in creation order (the `clients` map is
  // keyed by BOTH privateId and publicId, so it cannot be iterated as a client
  // list). Used to stop this env's clients on teardown — see teardownEnv.
  const createdClients: MockClient[] = [];
  // One game-time meter per process, armed the moment the process exists (see environment-lookup.ts).
  const gameTimeMeters = new GameTimeMeters();
  const canvasesShared: HTMLCanvasElement[] = [];

  // ── Connector identity layer ──────────────────────────────────────────────
  // The mock analogue of a gotti.config `connectors` block. Every mock client
  // is seated on one of these connector ids; `killConnector` severs one, which
  // drives the REAL area detach + client reconnect paths (Task 2 / Task 5).
  const connectorIds: string[] = (opts.connectors && opts.connectors.length
    ? opts.connectors
    : [{ id: 'mock_connector' }]
  ).map((c) => String(c.id));
  const deadConnectors = new Set<string>();
  // Connectors mid-graceful-removal (env.removeConnector): the gate stops
  // SELECTING them (new joins + reconnect targets) but dispatch is NOT yet
  // severed — their existing clients keep being served until they migrate.
  const drainingConnectors = new Set<string>();
  // connectorId → its area/type scope (from env.addConnector({ areas })). Absent
  // or empty ⇒ unscoped (matches every area), mirroring connectorScopeMatches.
  const connectorScopes = new Map<string, (string | number)[]>();
  // publicId/instance → current connector id. Keyed by the client INSTANCE so
  // it is stable before authenticate() assigns a publicId and immune to the
  // duplicate privateId/publicId keys in the `clients` map.
  const clientConnectorMap = new Map<MockClient, string>();

  const connectorLoad = (id: string): number => {
    let n = 0;
    for (const v of clientConnectorMap.values()) if (v === id) n++;
    return n;
  };
  // True when connector `id`'s scope covers EVERY target area (unscoped ⇒ always).
  const connectorInScope = (id: string, areas?: (string | number)[]): boolean => {
    if (!areas || !areas.length) return true;
    const scope = connectorScopes.get(id);
    return areas.every((aid) =>
      connectorScopeMatches(scope, aid, areaServers[String(aid)]?.areaFile?.type ?? null),
    );
  };
  const leastLoadedLiveConnector = (areas?: (string | number)[]): string | undefined => {
    const live = connectorIds.filter(
      (id) => !deadConnectors.has(id) && !drainingConnectors.has(id) && connectorInScope(id, areas),
    );
    if (!live.length) return undefined;
    // Deterministic: min load, ties broken by declaration order.
    return live.reduce((best, id) => (connectorLoad(id) < connectorLoad(best) ? id : best), live[0]);
  };

  // ── The gate's client→area index (mock) ───────────────────────────────────
  // The REAL gate owns the "which areas is this client seated in" index and is
  // the ONLY thing that decides where a reconnecting client is put back
  // (GateServer.handleClientAreaChanged → rejoinAreaIdsFor → the seat's
  // rejoinAreaIds). It is fed exclusively by the CONNECTOR
  // (ConnectorServer.handleClientJoinedArea/removeClientFromArea →
  // reportClientAreaChange), never by the client — and it records each area's
  // grace window, because only a seat-HOLDING area (graceMs > 0) is ever offered
  // back on a rejoin.
  //
  // Mirrored here: the mock's own connector-side seam (the patched handleClient
  // Join/handleClientLeave, which are where this env's areas report membership)
  // maintains it, and the rejoin flow reads it. Keyed by publicId — the mock's
  // stable per-client identity — where the real one keys by privateId; the real
  // index survives that difference by moving the record on an identity resume,
  // which the single-identity mock has no analogue for.
  const gateClientAreas = new Map<string, Map<string, number>>();   // publicId → areaId → graceMs
  // Set by the gate wiring below (when this env was given a gate) so the connector
  // topology the gate knows about tracks addConnector/removeConnector/killConnector.
  let gateSyncHook: (() => void) | null = null;
  const reportClientAreaChange = (publicId: string | number, areaId: string, joined: boolean, graceMs: number) => {
    const key = String(publicId);
    // A real gate learns this ONLY from the connector, over the wire. When this
    // env has one, send it the same report so its index — the thing a real rejoin
    // is routed from — is populated for real rather than diverging silently.
    //
    // Reported AS the connector the gate has this client seated on. The gate only
    // honours a report from that connection (a dying connector's late report must
    // not drop a live area), and in production that IS the reporting connector —
    // but this env deliberately keeps its own seating authority (see the gate
    // wiring below), so the two ids can differ and every report would be dropped.
    //
    // NOTHING here is skipped or warned away: the mock index below updates
    // unconditionally, so any quietly-dropped report is the two indexes silently
    // disagreeing — the exact failure mode that makes a harness lie.
    if (gate) {
      const reporting = clients[String(publicId)];
      const record = reporting?.privateId
        ? (gate as any).authenticatedClients?.[reporting.privateId]
        : undefined;
      if (!record) {
        // No gate record for this session AT ALL — it was purged/retired (a
        // deliberate leave, an expired hold, an identity resume that retired it).
        // The REAL gate's handleClientAreaChanged returns early on exactly this,
        // so there is no index to keep in sync and nothing to report.
      } else if (record.connectorId == null) {
        throw new Error(
          `[test-utils] cannot report CLIENT_AREA_CHANGED for client ${String(publicId)} ` +
          `(area ${areaId}, joined=${joined}): the gate holds an auth record for it but no ` +
          `connectorId, so the report cannot be attributed and the gate's rejoin index would ` +
          `silently diverge from this env's. A seat must be granted (reserveSeat) before an ` +
          `area reports membership.`
        );
      } else {
        void Promise.resolve(
          gate.onServerMessage(
            { id: record.connectorId, type: SERVER_TYPES.CONNECTOR } as any,
            [GATE_TO_CONNECTOR_PROTOCOLS.CLIENT_AREA_CHANGED, reporting.privateId, areaId, joined, graceMs],
          ),
        ).catch((err) => {
          // Rethrown, never logged-and-dropped: this surfaces as an unhandled
          // rejection and fails the run.
          throw new Error(
            `[test-utils] gate CLIENT_AREA_CHANGED report failed for client ${String(publicId)} ` +
            `(area ${areaId}, joined=${joined}): ${err instanceof Error ? err.message : String(err)}`
          );
        });
      }
    }
    if (!joined) {
      const held = gateClientAreas.get(key);
      held?.delete(areaId);
      if (held && !held.size) gateClientAreas.delete(key);
      return;
    }
    const held = gateClientAreas.get(key) ?? new Map<string, number>();
    held.set(areaId, graceMs > 0 ? graceMs : 0);
    gateClientAreas.set(key, held);
  };
  /** The SERVER's rejoin list: areas holding a seat for this client (graceMs > 0). */
  const heldAreaIdsFor = (publicId: string | number): string[] =>
    [...(gateClientAreas.get(String(publicId)) ?? new Map<string, number>())]
      .filter(([, graceMs]) => graceMs > 0)
      .map(([areaId]) => areaId);

  // Options for a REJOIN's area handshake. Mirrors buildJoinOptions below plus
  // the SEAT's server-set `rejoin`/`previousConnectorId` pair: the real gate
  // stamps both onto the seat in reserveSeat, and ConnectorServer merges the
  // seat's options over whatever the client sent — which is why an area sees the
  // flag at all. It is VISIBILITY only (`onClientConnected` reads it): a held
  // rejoin is admitted by the held seat itself, never by a hook's verdict.
  const buildRejoinJoinOptions = (client: MockClient, areaId: string, previousConnectorId?: string): Record<string, any> => {
    const reserved = client.reservedSeatOptions;
    const authData = (client.auth?.data ?? {}) as Record<string, any>;
    const baseOptions = reserved && typeof reserved === 'object' && !Array.isArray(reserved) ? { ...reserved } : {};
    return { id: client.id, areaId, ...authData, ...baseOptions, rejoin: true, previousConnectorId };
  };

  // The rejoin handshake for one reconnect — the mock's stand-in for
  // ConnectorServer.handleGetFirstAreas, and nothing more. The areas come from
  // the SERVER's index above (never from the client, which asserts nothing), and
  // each one is driven through the REAL reconnect transport entry point:
  // PROTOCOLS.CLIENT_JOINED_AREA → AreaServer.handleReconnectTransportJoin,
  // which is what the real connector sends. NOT handleTryJoinArea — that is
  // REQUEST_JOIN_AREA, the client-initiated `joinArea()` path, and using it here
  // is how this harness used to imply a rejoin could be adjudicated. It cannot:
  // an area holding a seat rebinds unconditionally, so there is no verdict to
  // inspect and no 'rejected' outcome on this path at all (per-area reconnect
  // outcomes are a rebind or 'area-gone'; see Client.onReconnectFailure).
  // Deferred one macrotask first so the detach state is observable
  // (detachedClients.size === 1) before the rebind lands, modelling the
  // client→gate→connector→area round trip.
  const runRejoinFlow = async (client: MockClient, connectorId: string, previousConnectorId: string | undefined, _options: any) => {
    const rejoinAreaIds: string[] = heldAreaIdsFor(client.id as string);
    await new Promise<void>((r) => setTimeout(r, getNumber(client.simulatedLatency ?? config?.clientServerLatency)));
    for (const areaId of rejoinAreaIds) {
      const area = areaServers[areaId];
      if (!area) {
        // The area is gone (e.g. a dynamic instance torn down), so the server
        // cannot route the client back into it. The real client derives this
        // itself, by diffing its pending rejoins against the area list
        // GET_FIRST_AREAS answers with; the mock calls the same private endpoint
        // of that derivation (failRejoinArea → onReconnectFailure('area-gone') +
        // tear down the leaked client-side area state) rather than restating the
        // diff, so there is one implementation of the outcome, not two.
        (client as any).failRejoinArea?.(areaId, 'area-gone');
        continue;
      }
      area.onServerMessage(
        { id: connectorId, type: SERVER_TYPES.CONNECTOR } as any,
        [PROTOCOLS.CLIENT_JOINED_AREA, client.id, client.auth.data, buildRejoinJoinOptions(client, areaId, previousConnectorId)],
      );
    }
  };

  // The in-process connector handed to `client.process.initialize`. Only the
  // surface the real Client/ClientProcess/reconnect driver touch is modelled;
  // system-level dispatch is overridden separately via CLIENT_SYSTEM_PROTO, so
  // the send/peer methods here are inert (parity with the old `{}` object,
  // which every existing client already ran on).
  class MockClientConnector {
    public onConnectionLost?: () => void;
    public connectedPeerIds: Array<string | number> = [];
    public peerConnections: Record<string, any> = {};
    public peerFailures: Record<string, any> = {};
    /** `previousConnectorId` is set only for a connector built by a REJOIN — the
     *  gate stamps it onto the re-reserved seat, so it reaches the area's
     *  onClientConnected exactly as it does in production. */
    constructor(public readonly client: MockClient, public connectorId: string, public previousConnectorId?: string) {}
    async handleConnect(_opts?: any) { return true; }
    async connect(_opts?: any) { return true; }
    onDisconnect() {}
    // Deliberate teardown: clear onConnectionLost FIRST so it never fires the
    // reconnect driver (mirrors BaseClientConnector.disconnect()).
    disconnect() { this.onConnectionLost = undefined; }
    send(_data?: any) {}
    sendRaw(_data?: any) {}
    async pingForFirstAreas(options?: any) { return runRejoinFlow(this.client, this.connectorId, this.previousConnectorId, options); }
    // Inert peer surface (mock CLIENT_SYSTEM_PROTO overrides real dispatch).
    sendAllPeersMessage() {}
    sendToPeers() {}
    sendToPeer() {}
    sendPeerRaw() {}
    sendPeersRaw() {}
    stopPeerConnection() {}
    stopAllPeerConnections() {}
    requestPeerConnection() {}
    async requestServer() { return undefined; }
    removeAllListeners() {}
  }

  const connectorRuntime: ConnectorRuntime = {
    ids: connectorIds,
    dead: deadConnectors,
    assignInitial: (opts) => {
      const { explicit, prefer, areas } = opts ?? {};
      const forced = prefer ?? explicit;
      if (forced != null) {
        if (!connectorIds.includes(forced)) {
          throw new Error(`[test-utils] createClient: unknown ${prefer != null ? 'preferConnector' : 'connectorId'} "${forced}" (configured: ${connectorIds.join(', ')})`);
        }
        // preferConnector is scope-checked against the join target; connectorId
        // is a raw force (back-compat) so it is not scope-checked.
        if (prefer != null && areas?.length && !connectorInScope(prefer, areas)) {
          throw new Error(`[test-utils] createClient: preferConnector "${prefer}" is out of scope for area(s) [${areas.join(', ')}] (connector scope: ${JSON.stringify(connectorScopes.get(prefer) ?? 'all')})`);
        }
        return forced;
      }
      // Route by scope + least-loaded among live in-scope connectors; fall back to
      // any live connector (degenerate config where none is in scope) then, last,
      // the historical default so a client is always seated somewhere.
      return leastLoadedLiveConnector(areas) ?? leastLoadedLiveConnector() ?? connectorIds[0] ?? 'mock_connector';
    },
    leastLoadedLive: leastLoadedLiveConnector,
    register: (client, connectorId) => {
      clientConnectorMap.set(client, connectorId);
      (client as any).connectorId = connectorId;
    },
    isDeadFor: (client) => !!client && deadConnectors.has((client as any).connectorId),
    makeConnector: (client, connectorId, previousConnectorId) => new MockClientConnector(client, connectorId, previousConnectorId),
  };

  /**
   * Simulate a connector server dying (see `MockEnvironment.killConnector`).
   * Marks it dead (dispatch severed), then runs the REAL area path
   * (`handleRemoveConnector` → `detachClient`, unpatched) on every area and
   * fires each affected client's REAL `handleConnectionLost` reconnect driver.
   */
  const killConnector = (id: string) => {
    if (!connectorIds.includes(id)) {
      throw new Error(`[test-utils] killConnector: unknown connector "${id}" (configured: ${connectorIds.join(', ')})`);
    }
    if (deadConnectors.has(id)) return;
    deadConnectors.add(id);
    gateSyncHook?.();       // the gate drops a dead connector from its topology
    // Areas detach (NOT evict) this connector's clients — the real Task 2 path.
    Object.values(areaServers).forEach((area) => {
      (area as any).handleRemoveConnector?.({ id });
    });
    // Kick each affected client's real reconnect driver.
    for (const [client, connectorId] of clientConnectorMap) {
      if (connectorId === id) (client as any).connector?.onConnectionLost?.();
    }
  };

  /**
   * Register a new LIVE connector at runtime (the in-process analogue of the
   * Starter's `addConnectorAtRuntime` — Task 8). `areas` scopes it: new joins
   * only route here for in-scope areas (consulted by the mock gate's scope +
   * least-loaded selection), mirroring `connectorScopeMatches`. An empty/absent
   * `areas` ⇒ unscoped (matches everything). Throws for a duplicate id.
   */
  const addConnector = (cfg: { id: string; areas?: (string | number)[] }) => {
    const id = String(cfg.id);
    if (connectorIds.includes(id)) {
      throw new Error(`[test-utils] addConnector: connector "${id}" already exists (configured: ${connectorIds.join(', ')})`);
    }
    connectorIds.push(id);
    if (cfg.areas && cfg.areas.length) connectorScopes.set(id, [...cfg.areas]);
    // A re-added id (previously removed) must shed any stale dead/draining flag.
    deadConnectors.delete(id);
    drainingConnectors.delete(id);
    gateSyncHook?.();       // the gate can seat on it from now on
  };

  // Default drain budget (ms) for a graceful removeConnector — a live tuning
  // value, mirrors the Starter's `removeConnectorAtRuntime` default (Task 9).
  const DEFAULT_MOCK_DRAIN_MS = 10_000;

  /**
   * Gracefully remove a connector at runtime (the in-process analogue of the
   * Starter's `removeConnectorAtRuntime` — Task 9). Mirrors that ordering:
   *   1. mark DRAINING — the mock gate stops selecting it (new joins + reconnect
   *      targets exclude it) while its existing clients are still served,
   *   2. detach its clients from every area via the REAL `handleRemoveConnector`
   *      → `detachClient` path (so the rejoin REBINDS instead of joining fresh —
   *      no `onClientLeft`, no double `addClient`),
   *   3. nudge each client's REAL `handleConnectionLost` reconnect driver, which
   *      re-gates onto a surviving in-scope connector,
   *   4. wait until no client remains seated on it (or `drainMs`),
   *   5. mark DEAD + drop it from the topology.
   * A clean drain fires ZERO `onClientLeft` — the areas rebind within grace.
   */
  const removeConnector = async (id: string, opts?: { drainMs?: number }) => {
    const cid = String(id);
    const drainMs = opts?.drainMs ?? DEFAULT_MOCK_DRAIN_MS;
    if (!connectorIds.includes(cid)) {
      throw new Error(`[test-utils] removeConnector: unknown connector "${cid}" (configured: ${connectorIds.join(', ')})`);
    }
    if (deadConnectors.has(cid) || drainingConnectors.has(cid)) return; // already gone / in progress
    // 1) Draining: gate stops seating here (new joins + reconnect target selection).
    drainingConnectors.add(cid);
    // 2) Areas detach (NOT evict) this connector's clients — the real detach path,
    //    so a rejoin within grace rebinds them (mirrors killConnector, but the
    //    connector is only draining, not dead: its dispatch stays live meanwhile).
    Object.values(areaServers).forEach((area) => {
      (area as any).handleRemoveConnector?.({ id: cid });
    });
    // 3) Nudge each affected client's real reconnect driver → surviving connector.
    for (const [client, connectorId] of clientConnectorMap) {
      if (connectorId === cid) (client as any).connector?.onConnectionLost?.();
    }
    // 4) Wait until every client has migrated off `cid`, or the drain budget lapses.
    const start = Date.now();
    while ([...clientConnectorMap.values()].includes(cid) && Date.now() - start < drainMs) {
      await new Promise<void>((r) => setTimeout(r, 10));
    }
    // 5) Mark dead (any straggler dispatch now dropped) + drop from the topology.
    deadConnectors.add(cid);
    drainingConnectors.delete(cid);
    connectorScopes.delete(cid);
    const idx = connectorIds.indexOf(cid);
    if (idx >= 0) connectorIds.splice(idx, 1);
    gateSyncHook?.();       // gone from the gate's topology too
  };

  const clientPrototypeState: PlaygroundClientPrototypeState = {
    gate,
    clients,
    mockHttpReq,
    clientGameData,
    clientGames,
    serverGameType: (serverGame.gameFile as ServerGameFile).type as string,
    gameId: GAME_ID,
    areas,
    config,
    master,
    connectorRuntime,
  };
  // NOTE: currentClientPrototypeState is published BELOW — only AFTER the server
  // `areaServers` map is populated (see the `await Promise.all(serverAreas…)`).
  // Publishing here (before that async area init) exposed a MODULE-GLOBAL whose
  // `areas` map was still EMPTY; a CONCURRENT boot (the live preview and a test
  // boot both call `initializer`) could read the global during that window and a
  // join would throw "Area <id> not found". Publishing post-population guarantees
  // the global is only ever a fully-populated state.

  // Delay importing @gotti/client until now and shim window to avoid Node ReferenceError during module eval
  const hadWindow = typeof (globalThis ).window !== "undefined";
  if (!hadWindow) {
    (globalThis ).window = { location: { origin: "" } } as any;
  }
  const clientMod = await import("@gotti/client");
  const Client: typeof ClientType = (clientMod ).Client;
  const CLIENT_SYSTEM_PROTO: Record<string, any> = (clientMod as any).CLIENT_SYSTEM_PROTO;
  const ClientProcessCtor: any = (clientMod as any).ClientProcess;
  ensureClientPrototypeOverrides(Client as typeof ClientType);
  // Same seam as ServerProcess.ensureAssetManager above: ClientProcess lazily
  // builds+memoizes its own manager the first time a system is added (also
  // where $assets gets attached, and where a game/area file's `preloads` get
  // resolved before onInit) — resolve that memoized field to the SAME shared
  // manager so client and server systems in this env see one asset world.
  ClientProcessCtor.prototype.ensureAssetManager = function (this: any): ServerAssetManager {
    if (!this.assetManager) this.assetManager = assetManager;
    return this.assetManager;
  };
  // Note: Keep the shim if it wasn't present originally, so later Client constructions can read window.location

  // ── Dynamic areas (createArea / destroyArea) ──────────────────────────────
  // The mock env has no master — the initializer itself plays the master's
  // role for dynamic areas: createArea boots a REAL in-process AreaServer from
  // a registered `dynamic: true` template (exactly like the static boot loop
  // below) and inserts it into the shared `areas` map, which makes every
  // patched transport (replaceClientArea / dispatchToArea / joinArea / ...)
  // work on it unchanged.
  const dynamicAreaKeys = new Map<string, string>(); // `${type}::${key}` → areaId
  const pendingWhenEmptyDestroys = new Map<string, { opts: { drain?: boolean, reason?: string }, timer: ReturnType<typeof setTimeout> }>();

  const bootMockAreaServer = async ({ areaFile, id, configData, gameId = GAME_ID }: MockServerAreaConfig & { id: string }) => {
    validateMockReconnectConfig(areaFile, serverGame.gameFile, id);
    const areaServer: MockAreaServer = new AreaServer({
      id,
      inProcessOnly: true,
      host: "mock",
      port: 0,
      // Reconnect grace is NOT a server option any more (`reconnectGraceMs` was
      // deleted from AreaServerConfigOptions): the policy is declared on the
      // AREA FILE (`reconnect: { enabled, graceMs }`) with the GAME FILE as the
      // default, and `AreaServer.reconnectGraceMs` resolves that precedence off
      // the two files below — which the mock passes through untouched. So a mock
      // area holds seats iff its own areaFile/gameFile say so, exactly like a
      // deployed one.
      options: { gameConfigData: (gameData ).configData, areaConfigData: configData, gameFile: serverGame.gameFile, gameId, areaFile },
    }) as MockAreaServer;
    areaServer.clients = {};
    areaServer.clientAreaFile = clientAreaFiles.find((a) => a.type === areaFile.type);
    if (id in areaServers) throw new Error(`Area with id:${id} already exists`);
    areaServers[id] = areaServer;
    await areaServer.initialize();
    if (areaServer.gameProcess) gameTimeMeters.arm(areaServer.gameProcess as any);
    return areaServer;
  };

  const teardownMockArea = async (areaId: string, opts: { drain?: boolean, reason?: string } = {}) => {
    const target = areaServers[areaId];
    if (!target) return;
    const pending = pendingWhenEmptyDestroys.get(areaId);
    if (pending) { clearTimeout(pending.timer); pendingWhenEmptyDestroys.delete(areaId); }
    // Drain-hook parity: production runs onDrain only for a drain-flagged
    // teardown (whenEmpty implies drain) — a plain destroyArea(id) must NOT
    // fire it in the playground either, or games pass here and diverge live.
    if (opts.drain) {
      try { await (target.areaFile as any)?.onDrain?.(target as any, opts.reason ?? 'destroy'); }
      catch (err) { console.warn(`[test-utils] onDrain threw during destroyArea(${areaId}):`, err); }
    }
    // Kick any stragglers so their client processes see a clean leave.
    Object.keys(target.clients ?? {}).forEach((clientId) => target.removeClientFromArea(clientId, undefined));
    target.gameProcess?.stopLoop?.();
    delete areaServers[areaId];
    for (const [key, id] of dynamicAreaKeys) { if (id === areaId) dynamicAreaKeys.delete(key); }
    const removedType = target.areaFile?.type as string;
    Object.values(areaServers).forEach((other) => {
      try {
        other.gameProcess?.dispatchGameAreaRemoved?.(areaId, removedType);
        (other.areaFile as any)?.onAreaRemoved?.(other as any, { areaId, type: removedType, gameId: (target as any).gameId, reason: opts.reason });
      } catch (err) { console.warn(`[test-utils] onAreaRemoved hook threw:`, err); }
    });
  };

  // Completes an armed destroyArea({ whenEmpty }) once its area empties —
  // called from the handleClientLeave patch below. Emptiness alone NEVER
  // triggers a teardown; only an explicit destroyArea call arms this.
  const maybeCompleteWhenEmptyDestroy = (area: MockAreaServer) => {
    const areaId = area.areaId as string;
    const pending = pendingWhenEmptyDestroys.get(areaId);
    if (!pending) return;
    if (Object.keys(area.clients ?? {}).length > 0) return;
    void teardownMockArea(areaId, pending.opts);
  };

  (AreaServer.prototype as any).createArea = async function (type: string, createOpts: { key?: string, gameId?: string, configData?: any, managed?: boolean, maxClients?: number, emptyTtlMs?: number } = {}) {
    const template = dynamicAreaTemplates.get(type);
    if (!template) {
      throw new Error(`[test-utils] createArea: no dynamic: true template for area type "${type}" — declare one in the playground's serverAreas.`);
    }
    const areaId = `${type}:${makeId(12)}`; // real master id format (DynamicAreaTable.create)
    const keyId = createOpts.key != null ? `${type}::${String(createOpts.key)}` : undefined;
    if (keyId != null) {
      const existing = dynamicAreaKeys.get(keyId);
      // An area with a pending whenEmpty destroy is DRAINING in production
      // terms — committed to destruction — so a same-key create re-points the
      // key instead of throwing (parity with Master.createArea).
      if (existing && !pendingWhenEmptyDestroys.has(existing)) {
        throw new Error(`Area of type "${type}" with key "${String(createOpts.key)}" already exists.`);
      }
      // Reserve BEFORE the async boot: production inserts byKey synchronously,
      // so a concurrent same-key create must throw, never double-boot.
      dynamicAreaKeys.set(keyId, areaId);
    }
    const effectiveGameId = createOpts.gameId ?? template.gameId ?? GAME_ID;
    let areaServer: MockAreaServer;
    try {
      areaServer = await bootMockAreaServer({
        ...template,
        id: areaId,
        configData: createOpts.configData ?? template.configData,
        gameId: effectiveGameId,
      });
    } catch (err) {
      // Roll back the reservation AND the half-booted zombie (bootMockAreaServer
      // registers into areaServers before initialize, mirroring the static boot).
      if (keyId != null && dynamicAreaKeys.get(keyId) === areaId) dynamicAreaKeys.delete(keyId);
      if (areaServers[areaId]) {
        areaServers[areaId].gameProcess?.stopLoop?.();
        delete areaServers[areaId];
      }
      throw err;
    }
    // Topology-add hooks on the OTHER live areas (mirrors AreaServer.updateGameAreas).
    Object.values(areaServers).forEach((other) => {
      if (other === areaServer) return;
      try {
        other.gameProcess?.dispatchGameAreaAdded?.(areaId, type);
        (other.areaFile as any)?.onAreaAdded?.(other as any, { areaId, type, gameId: effectiveGameId });
      } catch (err) { console.warn(`[test-utils] onAreaAdded hook threw:`, err); }
    });
    return { areaId };
  };

  // NOTE: ownership (creator-only destroy) is NOT enforced in the mock — the
  // single-process env has no per-server identity to authenticate requesters.
  (AreaServer.prototype as any).destroyArea = async function (areaId: string, destroyOpts: { drain?: boolean, whenEmpty?: boolean, timeoutMs?: number, reason?: string } = {}) {
    const target = areaServers[areaId];
    if (!target) return;
    if (destroyOpts.whenEmpty && Object.keys(target.clients ?? {}).length > 0) {
      const prior = pendingWhenEmptyDestroys.get(areaId);
      if (prior) clearTimeout(prior.timer);
      // whenEmpty implies drain at the final teardown (parity with Master).
      const drain = destroyOpts.drain ?? true;
      const timer = setTimeout(() => {
        pendingWhenEmptyDestroys.delete(areaId);
        void teardownMockArea(areaId, { drain, reason: destroyOpts.reason ?? 'when-empty-timeout' });
      }, destroyOpts.timeoutMs ?? 30000);
      (timer as any).unref?.();
      pendingWhenEmptyDestroys.set(areaId, { opts: { drain, reason: destroyOpts.reason ?? 'when-empty' }, timer });
      return;
    }
    await teardownMockArea(areaId, { drain: destroyOpts.drain, reason: destroyOpts.reason });
  };

  // AreaServer dispatch helpers tied into ordered delivery.
  //
  // Rejoin-aware, mirroring the REAL AreaServer.handleClientJoin (:1247-1266):
  // a rejoin within the grace window (client is in `detachedClients`) REBINDS
  // an already-held client — clear the detach bookkeeping, fire
  // onClientConnected({ joining:false }) and skip the membership block
  // (addClient / onClientJoined fire exactly once per membership, never on
  // reconnect). detachedClients / clearDetached are the REAL (unpatched)
  // AreaServer state + method, so the mock rejoin exercises the same rebind
  // machinery the real server does — the whole point of Task 6's fidelity.
  AreaServer.prototype.handleClientJoin = function (playerId: string, auth: any, connectorId: any, options: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const clientsMap = selfArea.clients ?? (selfArea.clients = {});
    const connectorLookup = selfArea.clientIdToConnectorIdLookup ?? ((selfArea as any).clientIdToConnectorIdLookup = {} as Record<string, string | number>);
    if (selfArea.areaFile?.serverOnly) throw new Error(`Cannot join area ${selfArea.areaId} as it is server only`);
    const connectorKey = connectorId ?? (clients[playerId] as any)?.connectorId ?? 'mock_connector';
    // Real AreaServer.handleClientJoin :1247-1248
    const rejoining = selfArea.detachedClients.has(String(playerId));
    if (rejoining) (selfArea as any).clearDetached(String(playerId));
    connectorLookup[playerId] = connectorKey;
    selfArea.addConnectorId?.(connectorKey);
    // The connector's membership report to the gate (ConnectorServer.handleClient
    // JoinedArea → reportClientAreaChange), carrying THIS area's grace window so
    // the gate knows whether it holds a seat here. This is the ONLY writer of the
    // rejoin list, exactly as in production.
    reportClientAreaChange(playerId, selfArea.areaId as string, true, selfArea.reconnectGraceMs);
    const globalClient = clients[playerId];
    if (globalClient) {
      clientsMap[playerId] = globalClient;
    }
    const targetClient = clients[playerId];
    const __joinLat = getNumber(targetClient?.simulatedLatency ?? config?.clientServerLatency);

    // IMPORTANT: Enqueue handleJoinedArea BEFORE calling addClient/onClientJoined.
    // Since orderedSetTimeout assigns sequence numbers in order of calling,
    // handleJoinedArea gets a lower sequence number than any dispatchToClient
    // calls triggered by onClientJoined. The queue processes in sequence order,
    // ensuring client area systems are initialized before messages are delivered.
    // (Mirrors the real sendToServer(CLIENT_JOINED_AREA) at :1250-1258, which
    // also precedes onClientConnected/onClientJoined; on a rejoin the client's
    // handleJoinedArea takes its idempotent rejoinArea + onReconnectSuccess path.)
    orderedSetTimeout(selfArea.areaId as string, playerId, unlessClosed(selfArea, targetClient, () => {
      targetClient?.handleJoinedArea(
        selfArea.areaId as string,
        selfArea.areaFile.type as string,
        selfArea.areaClientData,
        selfArea.areaState.client,
        options,
        !!selfArea.gameFile?.preventMultiClientAreas || !!selfArea.areaFile?.preventMultiClientAreas
      );
    }), __joinLat);

    // Connection hook fires on EVERY (re)connect (real :1260).
    selfArea.areaFile?.onClientConnected?.(selfArea, { id: playerId, auth, options, joining: !rejoining, connectorId: connectorKey });
    // Membership block: first join only (real :1261-1266). A rejoin must NOT
    // re-add the client to the game process or re-fire onClientJoined.
    if (!rejoining) {
      selfArea.gameProcess.addClient({ id: playerId, auth, options });
      selfArea.areaFile?.onClientJoined?.(selfArea, { id: playerId, auth, options });
    }
  };

  // Fires the connection hook before the membership hook, deduped against an
  // earlier detach, mirroring the REAL AreaServer.handleClientLeave (:1303-1319):
  // a client that was detached already had onClientDisconnected({leaving:false})
  // fired at detach time, so `clearDetached` returning true here suppresses a
  // second one (exactly one disconnect hook per connection episode); a
  // deliberate leave fires onClientDisconnected({leaving:true}) then onClientLeft.
  AreaServer.prototype.handleClientLeave = function (playerId: string, reason: any, options: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const clientsMap = selfArea.clients ?? (selfArea.clients = {});
    const connectorLookup = selfArea.clientIdToConnectorIdLookup ?? ((selfArea as any).clientIdToConnectorIdLookup = {} as Record<string, string | number>);
    // Real :1303-1304
    const wasDetached = (selfArea as any).clearDetached?.(String(playerId)) ?? false;
    if (!wasDetached) selfArea.areaFile?.onClientDisconnected?.(selfArea, playerId, { leaving: true, reason });
    const connectorKey = connectorLookup[playerId];
    // Cleanup FIRST, then onClientLeft — mirrors the REAL AreaServer.handleClient
    // Leave (:1335-1341: delete clientIdToConnectorIdLookup → gameProcess.remove
    // Client → removeConnectorId, ALL before onClientLeft). Game code inspecting
    // the area's maps from onClientLeft must see them already cleared, exactly as
    // the real server presents them (Task 6 review F2). The mock previously fired
    // onClientLeft BEFORE deleting the lookup/clientsMap entries.
    if (connectorKey !== undefined) delete connectorLookup[playerId];
    // The connector's leave report to the gate — membership actually ended, so
    // the area drops out of the rejoin list. A DETACH deliberately reports
    // nothing: the seat is still held, which is the whole point of the index.
    reportClientAreaChange(playerId, selfArea.areaId as string, false, 0);
    selfArea.gameProcess.removeClient(playerId, options);
    if (connectorKey !== undefined) selfArea.removeConnectorId?.(connectorKey);
    delete clientsMap[playerId];
    selfArea.areaFile?.onClientLeft?.(selfArea, playerId, reason, options);
    const targetClient = clients[playerId];
    orderedSetTimeout(selfArea.areaId as string, playerId, unlessClosed(selfArea, targetClient, () => {
      // Guard against a double client-side leave: during failover a client can
      // tear down its area first (reconnect gave up → Client.failRejoinArea →
      // handleLeftArea) before this server-side leave lands, and
      // Client.handleLeftArea throws on an already-left area (reads .type off an
      // absent joinedAreasById entry). Only deliver if the client is still in.
      if (targetClient?.process?.joinedAreasById?.[selfArea.areaId as string]) {
        targetClient.handleLeftArea(selfArea.areaId as string, reason, options);
      }
    }), getNumber(targetClient?.simulatedLatency ?? config?.clientServerLatency));
    // An explicit destroyArea({ whenEmpty }) may be waiting on this area emptying.
    maybeCompleteWhenEmptyDestroy(selfArea);
  };

  AreaServer.prototype.addClientToArea = function (playerId: any, areaId: any, opts: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const clientsMap = selfArea.clients ?? (selfArea.clients = {});
    const playerKey = String(playerId);
    const client = clientsMap[playerKey];
    if (client) {
      const toArea: MockAreaServer | undefined = areas[areaId];
      if (!toArea) {
        return false;
      }
      const connectorLookup = selfArea.clientIdToConnectorIdLookup ?? ((selfArea as any).clientIdToConnectorIdLookup = {} as Record<string, string | number>);
      const connectorKey = connectorLookup[playerKey] ?? (client as any).connectorId ?? 'mock_connector';
      orderedSetTimeout(selfArea.areaId as string, toArea.areaId as string, unlessClosed(selfArea, toArea, () => {
        toArea.handleClientJoin(playerId, client.auth.data, connectorKey, opts);
      }), getNumber(config?.serverServerLatency));
      if (selfArea.gameFile?.preventMultiClientAreas || selfArea.areaFile?.preventMultiClientAreas) {
        selfArea.removeClientFromArea(playerId, opts);
      }
      return true;
    }
    return false;
  };

  AreaServer.prototype.removeClientFromArea = function (playerId: any, opts: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const clientsMap = selfArea.clients ?? (selfArea.clients = {});
    const playerKey = String(playerId);
    const client = clientsMap[playerKey];
    if (client) {
      // handleClientLeave already enqueues the correct client-side leave
      // (client.handleLeftArea → leaveArea: resolves the area file, honours the
      // init-deferral, fires onLeft/onLeftArea with reason=REMOVED_BY_AREA, and
      // denits). A SECOND direct client.leaveArea here double-denitted the same
      // area (and passed the reason in the options slot) — harmless only while
      // the client's denitAreaFile tolerated a second call; it now throws
      // "No area type found", so drive the leave exactly once via handleClientLeave.
      selfArea.handleClientLeave(playerId, LEAVE_AREA_REASONS.REMOVED_BY_AREA, opts);
      return true;
    }
    return false;
  };

  AreaServer.prototype.dispatchToArea = function (areaId: any, message: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const toArea: MockAreaServer | undefined = areas[areaId];
    if (!toArea) {
      return;
    }
    orderedSetTimeout(selfArea.areaId as string, toArea.areaId as string, unlessClosed(selfArea, toArea, () => {
      if (message.to === undefined || !("to" in message)) {
        toArea.areaFile?.onAreaGameMessage?.(selfArea, selfArea.areaId as string, jsonCopy(message));
      } else {
        toArea.gameProcess.systemLookup[message.to]?.onAreaMessage?.(selfArea.areaId as string, jsonCopy(message));
      }
    }), getNumber(config?.serverServerLatency));
  };

  AreaServer.prototype.dispatchToClient = function (clientId: string, message: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const clientsMap = selfArea.clients ?? (selfArea.clients = {});
    const targetClient = clientsMap[clientId] ?? clients[clientId];
    if (!targetClient) {
      console.warn(`[test-utils] dispatchToClient: Client ${clientId} not found in area ${selfArea.areaId} or global clients`);
      return;
    }
    // A dead connector's clients are unreachable until they reconnect onto a
    // live one — mirror the real transport dropping sends to a dead connector.
    if (connectorRuntime.isDeadFor(targetClient)) return;
    orderedSetTimeout(selfArea.areaId as string, clientId, unlessClosed(selfArea, targetClient, () => {
      if (message.to === undefined || !("to" in message)) {
        targetClient.emitGameEvents?.(message.type, jsonCopy(message.data));
      } else {
        // deliverToSystem buffers when the client's game/area file init is
        // still awaiting (systems not yet registered) — a join-time dispatch
        // (e.g. player-list to a sync system) must not race that window.
        const payload = jsonCopy(message);
        const delivered = (targetClient.process as any).deliverToSystem
          ? (targetClient.process as any).deliverToSystem(message.to, (sys: any) => sys.onServerMessage?.(payload))
          : !!targetClient.process.systemLookup[message.to] && (targetClient.process.systemLookup[message.to].onServerMessage?.(payload), true);
        if (!delivered) {
            console.warn(`[test-utils] dispatchToClient: System ${message.to} not found on client ${clientId}`);
        }
      }
    }), getNumber(targetClient.simulatedLatency ?? config?.clientServerLatency));
  };

  AreaServer.prototype.dispatchToAllClients = function (message: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const clientsMap = selfArea.clients ?? (selfArea.clients = {});
    Object.values(clientsMap).forEach((targetClient: MockClient) => {
      // Skip clients on a dead connector (see dispatchToClient).
      if (connectorRuntime.isDeadFor(targetClient)) return;
      orderedSetTimeout(selfArea.areaId as string, targetClient.privateId, unlessClosed(selfArea, targetClient, () => {
        if (message.to === undefined || !("to" in message)) {
          targetClient.emitGameEvents?.(message.type, jsonCopy(message.data));
        } else {
          // Same init-window buffering as dispatchToClient — broadcasts (e.g.
          // position sync to every client) must reach late-joining clients
          // whose area systems are still registering.
          const payload = jsonCopy(message);
          const deliver = (sys: any) => sys.onServerMessage?.(payload);
          if ((targetClient.process as any).deliverToSystem) {
            (targetClient.process as any).deliverToSystem(message.to, deliver);
          } else {
            targetClient.process.systemLookup[message.to]?.onServerMessage?.(payload);
          }
        }
      }), getNumber(targetClient.simulatedLatency ?? config?.clientServerLatency));
    });
  };

  AreaServer.prototype.dispatchToAreas = function (areaIds: any[], message: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    areaIds.forEach((areaId) => selfArea.dispatchToArea(areaId, message));
  };

  // dispatchToClients (PLURAL): the real impl routes via clientIdToConnectorIdLookup/relayToClients
  // (real connector), which is unwired in the mock — so fan out to the mocked singular form.
  (AreaServer.prototype as any).dispatchToClients = function (clientIds: string[], message: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    (clientIds || []).forEach((id) => selfArea.dispatchToClient(id, message));
  };

  // requestArea: server area -> area request/response, routed to the target area system's
  // onAreaRequest (mirrors dispatchToArea for routing + requestClient for the request/response shape).
  (AreaServer.prototype as any).requestArea = function (areaId: any, message: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    return new Promise((resolve, reject) => {
      const toArea: MockAreaServer | undefined = areas[areaId];
      if (!toArea) return reject(new Error(`[test-utils] requestArea: area ${String(areaId)} not found`));
      orderedSetTimeout(selfArea.areaId as string, toArea.areaId as string, unlessClosed(selfArea, toArea, async () => {
        try {
          const sys: any = toArea.gameProcess.systemLookup[message.to];
          const handler = sys?.onAreaRequest;
          if (!handler) {
            return reject(new Error(`[test-utils] requestArea: no onAreaRequest for system ${String(message.to)} in area ${String(areaId)}`));
          }
          const res = await handler.call(sys, selfArea.areaId, jsonCopy(message));
          resolve(res);
        } catch (err) { reject(err); }
      }), getNumber(config?.serverServerLatency));
    });
  };

  // replaceClientArea: MOVE a client from this area into another — add to the new area AND remove
  // from this one, mirroring the real AreaServer.replaceClientArea. Removing from the old area is
  // essential: dispatchToServer routes to joinedAreas[0], so if the client stays in the matchmaker
  // after entering the dungeon, all its in-game messages (movement/input -> PLAYER_MOVEMENT) keep
  // hitting the matchmaker area (which lacks those systems) and get dropped.
  (AreaServer.prototype as any).replaceClientArea = function (playerId: any, areaId: any, opts: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    selfArea.addClientToArea(playerId, areaId, opts);
    selfArea.removeClientFromArea(playerId, opts);
    return true;
  };

  AreaServer.prototype.requestClient = async function (playerId: any, message: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    return new Promise(async (resolve, reject) => {
    const clientsMap = selfArea.clients ?? (selfArea.clients = {});
    const client = clientsMap[String(playerId)] ?? clients[String(playerId)];
      if (!client) return reject(new Error(`Client ${String(playerId)} not in area ${String(selfArea.areaId)}`));
      // A client on a dead connector is unreachable until it reconnects — mirror
      // the fire-and-forget dispatch skips (dispatchToClient/dispatchToAllClients)
      // by REJECTING rather than falsely resolving (Task 6 review F1).
      if (connectorRuntime.isDeadFor(client)) {
        return reject(new Error(`[test-utils] requestClient: client ${String(playerId)} is on a dead connector (unreachable until reconnect)`));
      }
      orderedSetTimeout(selfArea.areaId as string, client.privateId , unlessClosed(selfArea, client, async () => {
        try {
          // `to` may be a system id of 0 (falsy) — route by target system when
          // present (a truthy check would drop system 0). See dispatchToServer.
          if (message.to != null) {
            const toSystem: ClientSystemType = client.process.systemLookup[message.to] as ClientSystemType;
            const handler = toSystem?.onAreaRequest;
            if (!handler) throw new Error(`No handler on client for request to system ${message.to}`);
            const res = await handler.call(toSystem, selfArea.id, jsonCopy(message));
            return resolve(res);
          } else {
            const handler = (client as any)?.onServerGameRequest;
            if (!handler) throw new Error(`No client game-level request handler`);
            const res = await handler(jsonCopy(message));
            return resolve(res);
          }
        } catch (err) { return reject(err); }
      }), getNumber(client.simulatedLatency ?? config?.clientServerLatency));
    });
  };

  AreaServer.prototype.createChannel = function (channelId: string, clientIds?: string[], data?: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const channels = selfArea.channels ?? (selfArea.channels = {});
    const clientsMap = selfArea.clients ?? (selfArea.clients = {});

    if (clientIds) {
        for(let i = 0; i < clientIds.length; i++) {
            const clientId = clientIds[i];
            if(!clientsMap[clientId]) {
                throw new Error(`Client: ${clientId} was not in the area when creating channel: ${channelId}`);
            }
        }
    }

    if (channels[channelId]) return false;
    channels[channelId] = { id: channelId, clientIds: [], data };
    
    selfArea.gameProcess.systemArray.forEach((s: any) => {
      s.onChannelCreated?.(channelId);
    });

    if (clientIds) {
      clientIds.forEach(clientId => {
        selfArea.addClientToChannel(clientId, channelId);
      });
    }
    return true;
  };

  AreaServer.prototype.destroyChannel = function (channelId: string) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const channels = selfArea.channels ?? (selfArea.channels = {});
    const channel = channels[channelId];
    if (!channel) return false;

    channel.clientIds.forEach(clientId => {
      selfArea.gameProcess.systemArray.forEach((s: any) => {
        s.onClientLeftChannel?.(clientId, channelId, LEAVE_CHANNEL_REASONS.CHANNEL_DESTROYED);
      });
    });

    selfArea.gameProcess.systemArray.forEach((s: any) => {
      s.onChannelDestroyed?.(channelId);
    });

    delete channels[channelId];
    return true;
  };

  AreaServer.prototype.addClientToChannel = function (clientId: string, channelId: string) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const channels = selfArea.channels ?? (selfArea.channels = {});
    const clientsMap = selfArea.clients ?? (selfArea.clients = {});

    if(!clientsMap[clientId]) {
        throw new Error(`Client: ${clientId} was not in the area when adding to channel: ${channelId}`);
    }

    const channel = channels[channelId];
    if (!channel) return false;
    if (channel.clientIds.includes(clientId)) return false;
    channel.clientIds.push(clientId);

    selfArea.gameProcess.systemArray.forEach((s: any) => {
      s.onClientJoinedChannel?.(clientId, channelId);
    });
    return true;
  };

  AreaServer.prototype.removeClientFromChannel = function (clientId: string, channelId: string) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const channels = selfArea.channels ?? (selfArea.channels = {});
    const channel = channels[channelId];
    if (!channel) return false;
    const idx = channel.clientIds.indexOf(clientId);
    if (idx === -1) return false;
    channel.clientIds.splice(idx, 1);

    selfArea.gameProcess.systemArray.forEach((s: any) => {
      s.onClientLeftChannel?.(clientId, channelId, LEAVE_CHANNEL_REASONS.REMOVED);
    });
    return true;
  };

  AreaServer.prototype.dispatchToChannel = function (channelId: string, message: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const channels = selfArea.channels ?? (selfArea.channels = {});
    const channel = channels[channelId];
    if (!channel) return;
    channel.clientIds.forEach(pid => {
      selfArea.dispatchToClient(pid, message);
    });
  };

  AreaServer.prototype.requestChannel = async function (channelId: string, message: any) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const channels = selfArea.channels ?? (selfArea.channels = {});
    const clientsMap = selfArea.clients ?? (selfArea.clients = {});
    const channel = channels[channelId];
    if (!channel) return [];
    // Skip members on a dead connector before fanning out — mirrors
    // dispatchToChannel (which skips them via dispatchToClient's isDeadFor guard)
    // and keeps requestChannel from a false-success on an unreachable member
    // (Task 6 review F1). requestClient also rejects for them, so this is belt-
    // and-suspenders; the filter keeps the intent explicit.
    const promises = channel.clientIds
      .filter((pid: string) => !connectorRuntime.isDeadFor(clientsMap[String(pid)] ?? clients[String(pid)]))
      .map(pid => selfArea.requestClient(pid, message).catch(() => null));
    const results = await Promise.all(promises);
    return results.filter(r => r !== null);
  };

  AreaServer.prototype.getClientsInChannel = function (channelId: string) {
    const selfArea: MockAreaServer = this as MockAreaServer;
    const channels = selfArea.channels ?? (selfArea.channels = {});
    const channel = channels[channelId];
    if (!channel) return [];
    return [...channel.clientIds];
  };

  // ServerSystem dispatch helpers are bound from AreaServer via bindServerMethods(),
  // so the AreaServer.prototype patches above are sufficient.

  // Client system messaging mocks — patch CLIENT_SYSTEM_PROTO so decorate() copies these mock
  // implementations onto every system instance instead of the real connector-based ones.
  CLIENT_SYSTEM_PROTO.dispatchToAllPeers = function (this: any, message: any) {
    const selfClient: MockClient = this.client as unknown as MockClient;
    const lat = effectivePeerLatency(selfClient, config);
    Object.entries(selfClient.peerLookup).forEach(([pid, { client }]: any) => {
      const copy = jsonCopy(message);
      orderedSetTimeout(selfClient.privateId, String(pid), unlessClosed(selfClient, client, () => deliverPeerMessageToClient(client, selfClient.id, copy)), lat);
    });
  };
  CLIENT_SYSTEM_PROTO.dispatchToAllPeersAndServer = function (this: any, message: any) {
    this.dispatchToAllPeers(message);
    this.dispatchToServer(message);
  };
  CLIENT_SYSTEM_PROTO.instantDispatchToAllPeersAndServer = function (this: any, message: any) {
    this.dispatchToAllPeers(message);
    this.instantDispatchToServer(message);
  };
  CLIENT_SYSTEM_PROTO.dispatchToPeers = function (this: any, playerIds: any[], message: any) {
    const selfClient: MockClient = this.client as unknown as MockClient;
    const lat = effectivePeerLatency(selfClient, config);
    playerIds.forEach((pid: any) => {
      const peer = selfClient.peerLookup[pid];
      if (peer) {
        const copy = jsonCopy(message);
        orderedSetTimeout(selfClient.privateId, String(pid), unlessClosed(selfClient, peer.client, () => deliverPeerMessageToClient(peer.client, selfClient.id, copy)), lat);
      }
    });
  };
  CLIENT_SYSTEM_PROTO.dispatchToPeer = function (this: any, playerId: any, message: any) {
    const selfClient: MockClient = this.client as unknown as MockClient;
    const peer = selfClient.peerLookup[playerId];
    if (peer) {
      const copy = jsonCopy(message);
      orderedSetTimeout(selfClient.privateId, String(playerId), unlessClosed(selfClient, peer.client, () => deliverPeerMessageToClient(peer.client, selfClient.id, copy)), effectivePeerLatency(selfClient, config));
    }
  };
  CLIENT_SYSTEM_PROTO.addPeer = async function (this: any, peerPlayerId: string, options = {}, _ackTimeout = 3000, _requestTimeout = 5000) {
    if (!this.onPeerSystemConnectionRequest) throw new Error(`Cannot add a peer from the system ${this.name}, it does not implement onPeerSystemConnectionRequest`);
    const selfClient: MockClient = this.client as unknown as MockClient;
    if ((selfClient).peerLookup[peerPlayerId]) throw new Error(`Already existing peer connection for peer:, ${peerPlayerId}`);
  const peerKey = String(peerPlayerId);
  const peerClient = clients[peerKey];
  if (!peerClient) throw new Error(`Peer ${peerPlayerId} not found`);
    // A request between the two in flight — this side's (production's requester holds the pending connection from the
    // moment it sends: requestPeerConnection throws), or the peer's to this side (this side holds the requester's
    // connection while its system decides) — refuses this one before it is sent; no system is asked.
    if (peerRequestInFlight(selfClient, peerClient) || peerRequestInFlight(peerClient, selfClient)) {
      throw new Error(`Already existing peer connection for peer:, ${peerPlayerId}`);
    }
    // Production's peer refuses a request while it still holds a connection to the requester
    // (BaseClientConnector.handlePeerConnectionRequest: "Already had peer connection for peer <requester>", relayed back
    // as the request's failure) — here, its end of a link this side already closed, until that close reaches it. Its
    // system is never asked.
    if (peerClient.peerLookup[selfClient.id]) throw new Error(`Already had peer connection for peer ${selfClient.publicId}`);
    const selfSystemName = this.name;
    let inFlight = peerRequestsInFlight.get(selfClient);
    if (!inFlight) peerRequestsInFlight.set(selfClient, (inFlight = new Set()));
    const requesting = inFlight;
    requesting.add(peerClient);
    return new Promise(async (resolve, reject) => {
      try {
        const resolved = await peerClient.process?.systemLookup[selfSystemName]?.onPeerSystemConnectionRequest?.(selfClient.publicId, options);
        // Decided: the request is no longer in flight, accepted or not.
        requesting.delete(peerClient);
        if (resolved) {
          // Both ends open together; `connectedAt` is what getPeerStatus reports while the link lasts.
          const connectedAt = Date.now();
          selfClient.peerLookup[peerPlayerId] = { client: peerClient, system: selfSystemName, connectedAt };
          peerClient.peerLookup[selfClient.id ] = { client: selfClient, system: selfSystemName, connectedAt };

          fireVirtualPeerConnectionStarted({
            initiator: selfClient,
            initiatorSeesPeerAs: String(peerPlayerId),
            responder: peerClient,
            responderSeesPeerAs: String(selfClient.id),
            systemName: selfSystemName,
            requestOptions: options,   // responder's started hooks get the initiator's request options
            acceptValue: resolved,     // initiator's started hooks get the responder's acceptance value
          });

          return resolve(resolved);
        } else {
          return reject(new Error(`Peer ${peerPlayerId} rejected connection`));
        }
      } catch (err) {
        requesting.delete(peerClient);
        return reject(err);
      }
    });
  };
  Object.defineProperty(CLIENT_SYSTEM_PROTO, "connectedPeers", { get(this: any) { return Object.keys(this.client.peerLookup); }, configurable: true });
  // Ends the link the way production does (BaseClientConnector.handlePeerFailure, reached through
  // peerConnection.onDisconnected when a side's data channel closes): each side drops ITS end — this side at once,
  // the peer when the close reaches its end, one peer latency later — and tells its own systems once: every one
  // `onAnyPeerConnectionEnded(otherId)`, the owning one `onPeerSystemConnectionEnded(otherId)`. Dropping the
  // `peerLookup` entries is what lets the pair link again (production reconnects; keeping them made `addPeer` throw
  // "Already existing peer connection"), and empties both peer lists, as apps/integration-tests
  // ClientToClient.test.ts asserts on real connectors.
  CLIENT_SYSTEM_PROTO.removePeer = function (this: any, playerId: any) {
    const selfClient: MockClient = this.client as unknown as MockClient;
    const info = selfClient.peerLookup[playerId];
    if (info) {
      const { client: peerClient, system: sysName } = info;
      const endLinkOn = (side: MockClient, otherId: string) => {
        side.process.systemArray.forEach((a: any) => { a.onAnyPeerConnectionEnded?.(otherId); });
        if (sysName !== undefined && sysName in side.process.systemLookup) {
          (side.process.systemLookup[sysName] as ClientSystemType)?.onPeerSystemConnectionEnded?.(otherId);
        }
        (side as any).handlePeerConnectionEnded?.(otherId);
      };
      // The peer's end of THIS link. Gone already — the peer removed the link too, before this close could reach it —
      // and the peer heard its own end then: there is no close to send. Still there, the close ends it when it
      // arrives — unless by then the peer removed it itself or the pair linked again, when the close is not about
      // the entry the peer holds and it hears nothing. Each side hears a link end exactly once.
      const peerEnd = peerClient.peerLookup[selfClient.id];
      delete selfClient.peerLookup[playerId];
      endLinkOn(selfClient, String(playerId));
      if (peerEnd === undefined) return;
      orderedSetTimeout(selfClient.privateId, String(playerId), unlessClosed(selfClient, peerClient, () => {
        if (peerClient.peerLookup[selfClient.id] !== peerEnd) return;
        delete peerClient.peerLookup[selfClient.id];
        endLinkOn(peerClient, String(selfClient.id));
      }), effectivePeerLatency(selfClient, config));
    }
  };
  // Production reads the ping off the side's connection to that peer, and -1 without one
  // (clientSystemMethods.getPeerPing): here, the link's simulated latency while this side's end is open.
  CLIENT_SYSTEM_PROTO.getPeerPing = function (this: any, playerId: any) {
    const selfClient = this.client as unknown as MockClient;
    return selfClient.peerLookup[playerId] ? effectivePeerLatency(selfClient, config) : -1;
  };
  // What production's getPeerStatus reads off the side's connection (clientSystemMethods.getPeerStatus): CONNECTED
  // with the link's `connectedAt` while this side's end is open; once it closed (BaseClientConnector.handlePeerFailure
  // deletes the connection), NONE. The mock records no failures (production does only under
  // `failedPeerRequestTrackerTimeout`).
  CLIENT_SYSTEM_PROTO.getPeerStatus = function (this: any, playerId: any) {
    const link = (this.client as unknown as MockClient).peerLookup[playerId];
    if (link) return { status: PEER_CONNECTION_STATUS.CONNECTED, connectedAt: link.connectedAt, failures: [] };
    return { status: PEER_CONNECTION_STATUS.NONE, failures: [] };
  };
  CLIENT_SYSTEM_PROTO.isConnectedToPeer = function (this: any, playerId: any) { return playerId in (this.client as unknown as MockClient).peerLookup; };
  CLIENT_SYSTEM_PROTO.dispatchToServer = function (this: any, message: any) {
    const selfClient: MockClient = this.client as unknown as MockClient;
    const payload: any[] = [selfClient.auth.privateId, message.type, message.data];
    // `to` may be a system id of 0 (falsy!) — the FIRST registered system gets
    // id 0, so a `if (message.to)` truthy check silently drops its routing target
    // and the server mis-handles the message as a game (not system) message. The
    // real connector always sends `to`; mirror that by omitting only a truly
    // absent target.
    if (message.to != null) payload.push(message.to);
    // Same resolution the real client uses (@gotti/client areaRouting): the
    // message goes to the area file that started THIS system, and throws when
    // that is ambiguous. This used to pick joinedAreasById[0], which agreed
    // with neither the old broadcast nor the real rule.
    const targetAreaId = (selfClient.process as any).resolveAreaForSystem(this.name);
    const toServer: MockAreaServer | undefined = areas[targetAreaId!];
    if (!toServer) throw new Error(`Client is not in any joined areas.`);
    orderedSetTimeout(selfClient.auth.privateId as string, targetAreaId!, unlessClosed(selfClient, toServer, () => {
      toServer.handleClientMessage(payload);
    }), getNumber(selfClient.simulatedLatency ?? config?.clientServerLatency));
  };
  CLIENT_SYSTEM_PROTO.instantDispatchToServer = function (this: any, message: any) { (this ).dispatchToServer(message); };
  CLIENT_SYSTEM_PROTO.dispatchToArea = function (this: any, areaId: any, message: any) {
    const selfClient: MockClient = this.client as unknown as MockClient;
    const payload: any[] = [selfClient.auth.privateId, message.type, message.data];
    // `to` may be a system id of 0 (falsy!) — the FIRST registered system gets
    // id 0, so a `if (message.to)` truthy check silently drops its routing target
    // and the server mis-handles the message as a game (not system) message. The
    // real connector always sends `to`; mirror that by omitting only a truly
    // absent target.
    if (message.to != null) payload.push(message.to);
    const toArea: MockAreaServer | undefined = areaServers[areaId];
    if (!toArea) throw new Error(`Client is not in any joined areas.`);
    orderedSetTimeout(selfClient.auth.privateId as string, areaId, unlessClosed(selfClient, toArea, () => {
      toArea.handleClientMessage(payload);
    }), getNumber(selfClient.simulatedLatency ?? config?.clientServerLatency));
  };
  CLIENT_SYSTEM_PROTO.requestArea = async function (this: any, message: any) {
    const selfClient: MockClient = this.client as unknown as MockClient;
    // No areaId → the area file that started this system (matching real SDK).
    if (message.areaId === undefined || message.areaId === null || message.areaId === '') {
      message.areaId = (selfClient.process as any).resolveAreaForSystem(this.name);
    }
    const toServer: MockAreaServer | undefined = areas[message.areaId];
    if (!toServer) {
      throw new Error(`Area ${String(message.areaId)} not found`);
    }
    return new Promise(async (resolve, reject) => {
      const lat = getNumber(selfClient.simulatedLatency ?? config?.clientServerLatency);
      orderedSetTimeout(selfClient.auth.privateId as string, message.areaId, unlessClosed(selfClient, toServer, async () => {
        try {
          const type = message.type;
          // `to` may be a system id of 0 (falsy) — route by target system when
          // present (a truthy check would drop system 0). See dispatchToServer.
          if (message.to != null) {
            const toSystem = toServer.gameProcess.systemLookup[message.to];
            const handler = toSystem?.onClientRequest;
            if (!handler) throw new Error(`No handler for request ${type} to system ${message.to}`);
            const res = await handler.call(toSystem, selfClient.auth.privateId, jsonCopy(message) );
            return resolve(res);
          } else {
            const handler = toServer.areaFile?.onClientGameRequest;
            if (!handler) throw new Error(`No handler for game request ${type}`);
            const res = await handler(toServer, selfClient.auth.privateId, jsonCopy({ type, data: message.data }));
            return resolve(res);
          }
        } catch (err) { return reject(err); }
      }), lat + lat);
    });
  };

  // Initialize STATIC server areas (dynamic templates were registered above and
  // only boot on demand via createArea).
  await Promise.all(staticServerAreas.map(async (cfg: MockServerAreaConfig) => {
    if (cfg.id == null) throw new Error('[test-utils] a static serverArea entry requires an id (or mark it dynamic: true)');
    await bootMockAreaServer(cfg as MockServerAreaConfig & { id: string });
  }));

  // Publish the client-prototype state NOW that `areaServers` is populated (see the
  // note where `clientPrototypeState` is built). `areas` is a live reference to
  // `areaServers`, so this object already sees every area added above — and no
  // client-prototype-patched method runs before this point (clients are created by
  // the caller's `createClients`, after this factory returns), so moving the publish
  // here changes nothing except closing the empty-`areas` race window.
  setClientPrototypeState(clientPrototypeState);

  // ── Gate wiring (transport stand-in ONLY) ─────────────────────────────────
  // The gate's LOGIC runs for real: `handleJoinGame` (identity matching →
  // `resumeSession` → the adopted publicId the client must connect under) and
  // `reserveSeat` (server-derived `rejoinAreaIds`, the seat's `rejoin`/
  // `previousConnectorId` pair, grace holds) are NOT replaced. They used to be —
  // `handleJoinGame` was overridden wholesale and its stand-in `reserveSeat`
  // handed back `publicId: client.id`, which (Client.id being a getter for
  // publicId) made the client's publicId adoption a guaranteed no-op, so this
  // harness could not exercise a resume at all while looking like it did.
  //
  // What is replaced is exactly the two things that need a network: the
  // connector REGISTRATION the gate would learn from a real connector server's
  // handshake, and the gate→connector RESERVE_SEAT round trip.
  if (gate) {
    // A connector registration, as `serverConnections` would hold it after a real
    // connector's registration request. `websocketOptions` is what handleJoinGame
    // derives host/port/connectorType from; `heartbeats.lastState` is what
    // getBestConnector reads (an absent `maxClients` = unlimited).
    const gateConnectorEntry = (id: string) => ({
      id, type: SERVER_TYPES.CONNECTOR, host: 'mock', port: 0,
      options: { websocketOptions: { host: 'mock', port: 0 } },
      preparedFor: true, status: 0,
      heartbeats: { lastState: { clientCount: connectorLoad(id), areas: Object.keys(areaServers) } },
    });
    const syncGateConnectors = () => {
      Object.keys(gate.serverConnections).forEach((id) => {
        if ((gate.serverConnections as any)[id]?.type === SERVER_TYPES.CONNECTOR) delete (gate.serverConnections as any)[id];
      });
      connectorIds.filter((id) => !deadConnectors.has(id)).forEach((id) => {
        (gate.serverConnections as any)[id] = gateConnectorEntry(id);
      });
    };
    gateSyncHook = syncGateConnectors;
    syncGateConnectors();
    // The ONLY gate transport seam: the RESERVE_SEAT request the gate sends to
    // the connector it picked. The connector answers with the game it seated the
    // client for, and holds the seat (options + rejoinAreaIds) until the client
    // connects — here the "connector" is the in-process MockClientConnector, so
    // the seat is simply stamped onto the client.
    //
    // Deliberate divergence: the mock does NOT re-seat the client onto the
    // connector the gate picked. `createClients({ connectorId })` / `preferConnector`
    // assign a connector BEFORE the join (they are how a failover test says where
    // a client lives), so `connectorRuntime` — not the gate — is this env's
    // seating authority. The gate's pick is real, it is just not what binds.
    gate.requestServer = (async (_connectorId: string | number, payload: any) => {
      if (Array.isArray(payload) && payload[0] === GATE_CONNECTOR_REQUEST_PROTOCOLS.RESERVE_SEAT) {
        const seat = payload[1];
        const seatedClient = clients[String(seat.privateId)];
        if (seatedClient) seatedClient.reservedSeatOptions = seat.options;
        return { gameId: seat.gameId, gameType: seat.gameType };
      }
      // A duplicate login TAKES OVER the live session: the gate tells the
      // connector holding it to drop that client. Modelled to the extent this env
      // has a transport — the socket goes away and the reconnect driver is not
      // allowed to bring it back. What is NOT modelled is the AREA-side eviction
      // a real ConnectorServer.removeClient would cascade (this env has no
      // ConnectorServer; areas are driven directly), so a taken-over client keeps
      // its area membership here.
      if (Array.isArray(payload) && payload[0] === GATE_CONNECTOR_REQUEST_PROTOCOLS.DISCONNECT_CLIENT) {
        const evicted = clients[String(payload[1])];
        if (!evicted) return false;
        (evicted as any).forceClosed = true;
        (evicted as any).connector?.disconnect?.();
        return true;
      }
      return undefined;
    }) as typeof gate.requestServer;
    // 5th arg = the game file's authIdentityPath, exactly as master pushes it on
    // ADD_GAME. Inert for a game file that declares none.
    gate.addGame((serverGame.gameFile ).type as string, GAME_ID, clientGameData, meta, (serverGame.gameFile as any)?.authIdentityPath);
  }

  const createClient = (clientEnvOptions?: any) => {
    // Ensure window exists during Client construction (mergeDefaultConfig uses window.location)
    const hadWinLocal = typeof globalThis.window !== "undefined";
    if (!hadWinLocal) {
      (globalThis ).window = { location: { origin: "" } } as any;
    }
    // `connectorId`/`preferConnector`/`__scopeAreas` are mock-env concerns, not
    // game env options — pull them out so they never leak into the Client's game
    // envOptions bag. `connectorId` is a raw force; `preferConnector` is scope-
    // checked against `__scopeAreas` (the join target areas, threaded by
    // createClients) and throws when out of scope.
    const { connectorId: explicitConnectorId, preferConnector, __scopeAreas, ...gameEnvOptions } = (clientEnvOptions ?? {}) as any;
    const gamesFilesLookup = (Array.isArray(opts.clientGame) ? opts.clientGame : [opts.clientGame]).reduce((acc, game) => {
      acc[GAME_ID] = game;
      return acc;
    }, {} as {[key: string]: ClientGameFile});

    const client: MockClient = new Client({ gateServerURL: "", requestTimeout: 2000 }, gamesFilesLookup , gameEnvOptions) as unknown as MockClient;
    if (!hadWinLocal) {
      // leave shim in place as some lazy paths might still check window later; do not delete to avoid races
    }
    // Every path that makes a client meets here — `env.createClient()`, both `createClients` forms,
    // `initClients` — so every registered creation hook sees the client now: before `onClientCreated`, before it
    // authenticates, before it joins. And before the client is recorded anywhere (the default-auth numbering,
    // the connector's load), so a hook that throws fails the creation and leaves no ghost client behind. With
    // none registered this is a no-op.
    runClientCreationHooks(client);
    // This client's connector identity (explicit override or least-loaded live), resolved BEFORE the client is
    // recorded anywhere: a refusal (an unknown connector, an out-of-scope preference) fails the creation and leaves no
    // trace of the client, as a creation hook's throw does. Registered below, once the client is recorded: stamped
    // onto the client and tracked for load/kill bookkeeping; its area joins seat on this id, and a connector death
    // re-picks a surviving one.
    const connectorId = connectorRuntime.assignInitial({ explicit: explicitConnectorId, prefer: preferConnector, areas: __scopeAreas });
    // Where test-utils records the client's canvas: from here on, a loss of its WebGL context is recorded from the
    // canvas's own `webglcontextlost` event, so `env.expectRendering` can report it without asking the canvas anything.
    watchContextLoss(gameEnvOptions.canvas);
    createdClients.push(client);
    gameTimeMeters.arm(client.process as any);
    client.peerLookup = {};
    connectorRuntime.register(client, connectorId);
    // keep mockEnv.clients in sync if a client clears its game
    const __origClearGame = (client as any).clearGame?.bind(client);
    (client as any).clearGame = function (...args: any[]) {
      try {
        __origClearGame?.(...args);
      } finally {
        const pid = (client as any)?.auth?.privateId ?? (client as any)?.privateId;
        if (pid) delete clients[String(pid)];
        const pubId = (client as any)?.auth?.publicId ?? (client as any)?.publicId;
        if (pubId) delete clients[String(pubId)];
      }
    };
    if (clientGame) {
      if(Array.isArray(clientGame)) {
        clientGame.forEach((cg) => {
          client.addGameFile?.(cg);
        });
      } else {
        client.addGameFile?.(clientGame);
      }
    }
    clientAreaFiles.forEach((a) => { client.addAreaFile?.(a); });

    return client;
  };

  const isClientPlaygroundConfigOptions = (
    value: CreateClientsOptions | ClientPlaygroundConfigOptions | undefined
  ): value is ClientPlaygroundConfigOptions => {
    if (!value || typeof value !== 'object') return false;
    return (
      'clientEnv' in value ||
      'clientAuth' in value ||
      'domSetup' in value ||
      'numberOfClients' in value ||
      // The playground's `layout` is a tiling name ('single' | 'grid' | 'stack'); the standard form's is a
      // number. `joinGame` cannot mark either form: both take it, and the standard callers that pass it
      // (the sandbox runners' `{ container, perClientEnvOptions, authenticate, joinGame }`) must stay standard.
      typeof value.layout === 'string'
    );
  };

  /**
   * Runs a declared `joinGame` for one freshly authenticated client. With no `options` the call is
   * `client.joinGame(gameId, areas)` with a BARE gameId — exactly what every caller made before
   * `options` existed — and with them the body is `{ gameId, ...options }`.
   */
  const joinClientToGame = async (client: MockClient, join: PlaygroundJoinGameOptions, ctx: { index: number; auth: any }) => {
    const gameId = join.gameId ?? GAME_ID;
    const body = typeof join.options === 'function' ? join.options(ctx) : join.options;
    const areas = typeof join.initialAreas === 'function' ? join.initialAreas(ctx) : join.initialAreas;
    await client.joinGame(body ? { gameId, ...body } : gameId, areas ?? []);
  };

  const createClientsWithStandardOptions = async (count: number, options: CreateClientsOptions = {}): Promise<MockClient[]> => {
    const {
      container,
      layout = 1,
      useDebugOnFirst = true,
      perClientEnvOptions,
      authenticate,
      onClientCreated,
      onJoinedArea,
      joinGame,
      connectorId,
      preferConnector,
      aspectRatio = 16 / 9,
    } = options;
    // The connector is chosen when the client is created, before it authenticates — a function-form
    // `initialAreas` (of `{ index, auth }`) cannot exist yet, so it cannot be scope-checked.
    if (preferConnector != null && typeof joinGame?.initialAreas === 'function') {
      throw new Error('[test-utils] createClients: preferConnector is scope-checked against joinGame.initialAreas before the client authenticates, so it needs a list, not a function of { index, auth }.');
    }
    const scopeAreas = Array.isArray(joinGame?.initialAreas) ? joinGame.initialAreas : undefined;

    const canvases: HTMLCanvasElement[] = canvasesShared;
    const startIndex = canvases.length;
    const hasDOM = typeof window !== 'undefined' && typeof document !== 'undefined';
    const containerEl: HTMLElement | null = hasDOM && container
      ? (typeof container === 'string' ? document.querySelector(container) : (container as HTMLElement))
      : null;
    // The canvases this call makes, one per client in order, placed in the container now (the layout splits it) but
    // recorded in `env.canvases` only once their client is created: see the loop below.
    const made: Array<{ canvas: HTMLCanvasElement; wrapper: HTMLElement }> = [];

    if (containerEl) {
      // Check if we already provided canvases in perClientEnvOptions - if so, don't create new ones
      // This allows external code (like SandboxRunner) to manage its own DOM/Canvas structure
      // while still using createClients to bootstrap the client logic.
      let hasProvidedCanvases = false;
      if (typeof perClientEnvOptions === 'function') {
        // Just check the first one to see if it has a canvas
        const opts = perClientEnvOptions(0);
        if (opts?.canvas) hasProvidedCanvases = true;
      } else if (perClientEnvOptions?.canvas) {
        hasProvidedCanvases = true;
      }
      
      const shouldCreateCanvases = !hasProvidedCanvases;

      if (shouldCreateCanvases) {
        const total = startIndex + count;
        const grid = calculateSplitScreenGrid({
          count: total,
          containerWidth: window.innerWidth,
          containerHeight: window.innerHeight,
          aspectRatio,
          minCanvasSize: 100, // Original code didn't have a minSize, keep minimal
        });

        applySplitScreenLayout({ container: containerEl, grid });
        // Override some styles for original test-utils styling
        containerEl.style.width = '100vw';
        containerEl.style.height = '100vh';

        for (let i = 0; i < count; i++) {
          const canvasIndex = startIndex + i;
          const { wrapper: div, canvas } = createCanvasWrapper({
            index: canvasIndex,
            idPrefix: 'canvas',
            canvasClass: 'split-screen-canvas',
            wrapperClass: 'split-screen-canvas-wrapper',
            backgroundColor: '#ccc', // Original was #ccc
            borderStyle: '1px solid #ccc',
            canvasWidth: grid.cellWidth,
            canvasHeight: grid.cellHeight,
          });

          made.push({ canvas, wrapper: div });
          containerEl.appendChild(div);
        }
      }
    }

    const created: MockClient[] = [];
    try {
      for (let i = 0; i < count; i++) {
        const envOpts = typeof perClientEnvOptions === 'function' ? perClientEnvOptions(i) : perClientEnvOptions;
        const baseEnv: any = { useDebug: useDebugOnFirst && i === 0, layout };
        const canvas = made[i]?.canvas;
        if (canvas) baseEnv.canvas = canvas;
        // The default auth id counts every client this env created, never `i`
        // (which restarts at 0 per call): a repeated id would land on an existing
        // `env.clients` key and replace that client instead of adding one.
        const defaultAuthIndex = createdClients.length;
        // Thread the connector override (if any) into createClient's assignment.
        // `preferConnector` is scope-checked against the join target areas
        // (`joinGame.initialAreas`), so an out-of-scope preference throws here —
        // before the client authenticates or joins.
        const client = await createClient({
          ...baseEnv,
          ...(envOpts || {}),
          ...(connectorId != null ? { connectorId } : {}),
          ...(preferConnector != null ? { preferConnector } : {}),
          ...(scopeAreas?.length ? { __scopeAreas: scopeAreas } : {}),
        });
        // The client exists: its canvas is the env's next one.
        if (canvas) canvases[startIndex + i] = canvas;
        if (onClientCreated) await onClientCreated(client, { index: i, canvas });
        if (onJoinedArea) {
          client.on('joined-area', async (area: any) => { await onJoinedArea(client, area); });
        }
        const authPayload = authenticate ? authenticate(i) : { id: `test_client_${defaultAuthIndex}`, username: `Player ${defaultAuthIndex}` };
        await client.authenticate(authPayload);
        if (joinGame) await joinClientToGame(client, joinGame, { index: i, auth: authPayload });
        created.push(client);
      }
    } catch (err) {
      // A creation failed: the canvases made for clients that were never created leave the page, unrecorded.
      for (const { canvas, wrapper } of made) {
        if (!canvases.includes(canvas)) wrapper.remove();
      }
      throw err;
    }
    return created;
  };

  const createClientsWithPlaygroundOptions = async (
    count: number,
    options: ClientPlaygroundConfigOptions<any, any>,
    baseOptions?: ClientPlaygroundConfigOptions<any, any>
  ): Promise<MockClient[]> => {
    const total = options.numberOfClients ?? baseOptions?.numberOfClients ?? count;
    if (total <= 0) return [];

    const canvases: HTMLCanvasElement[] = canvasesShared;
    const startIndex = canvases.length;
    const hasDOM = typeof window !== 'undefined' && typeof document !== 'undefined';

    const resolveElement = (ref: string | HTMLElement | undefined | null): HTMLElement | null => {
      if (!hasDOM || !ref) return null;
      if (typeof ref === 'string') {
        try {
          return document.querySelector(ref) as HTMLElement | null;
        } catch {
          return null;
        }
      }
      return ref instanceof HTMLElement ? ref : null;
    };

    // The canvases this call makes (in a setup container that holds none), recorded in `env.canvases` only once their
    // client is created; one made for a client that never is leaves the page again — see the loop below.
    const made = new Set<HTMLCanvasElement>();

    const ensureCanvasInContainer = (container: HTMLElement, id: string): HTMLCanvasElement => {
      // Use window dimensions first since container may not be laid out yet (display:none -> display:block timing)
      const targetWidth = window.innerWidth || container.clientWidth || 1280;
      const targetHeight = window.innerHeight || container.clientHeight || 720;

      if (container instanceof HTMLCanvasElement) {
        // Ensure canvas has proper pixel dimensions, not just CSS dimensions
        if (!container.width || container.width < 100) {
          container.width = targetWidth;
        }
        if (!container.height || container.height < 100) {
          container.height = targetHeight;
        }
        return container;
      }
      const existingById = container.querySelector(`#${id}`) as HTMLCanvasElement | null;
      if (existingById) {
        // Ensure existing canvas has proper pixel dimensions
        if (!existingById.width || existingById.width < 100) {
          existingById.width = targetWidth;
        }
        if (!existingById.height || existingById.height < 100) {
          existingById.height = targetHeight;
        }
        return existingById;
      }
      const firstCanvas = container.querySelector('canvas') as HTMLCanvasElement | null;
      if (firstCanvas) {
        // Ensure existing canvas has proper pixel dimensions
        if (!firstCanvas.width || firstCanvas.width < 100) {
          firstCanvas.width = targetWidth;
        }
        if (!firstCanvas.height || firstCanvas.height < 100) {
          firstCanvas.height = targetHeight;
        }
        return firstCanvas;
      }
      const canvas = document.createElement('canvas');
      canvas.id = id;
      // Set actual canvas pixel dimensions (critical for rendering)
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.maxWidth = '100%';
      canvas.style.maxHeight = '100%';
      canvas.style.border = '1px solid #ccc';
      container.appendChild(canvas);
      made.add(canvas);
      return canvas;
    };

    const normalizeDomSetupResult = (result: ClientPlaygroundDomSetupResult | undefined) => {
      if (!hasDOM || result == null) return { canvas: undefined, container: undefined };
      if (result instanceof HTMLCanvasElement) {
        return { canvas: result, container: result.parentElement ?? undefined };
      }
      if (result instanceof HTMLElement) {
        return result.tagName?.toLowerCase() === 'canvas'
          ? { canvas: result as HTMLCanvasElement, container: (result as HTMLCanvasElement).parentElement ?? undefined }
          : { canvas: undefined, container: result };
      }
      if (typeof result === 'string') {
        return { canvas: undefined, container: resolveElement(result) ?? undefined };
      }
      if (typeof result === 'object') {
        const container = resolveElement((result as any).container) ?? undefined;
        const canvasCandidate = (result as any).canvas;
        const canvas = canvasCandidate instanceof HTMLCanvasElement ? canvasCandidate : undefined;
        return { canvas, container };
      }
      return { canvas: undefined, container: undefined };
    };

    const resolveEnv = (
      cfg: ClientPlaygroundConfigOptions<any, any> | undefined,
      index: number,
      prevValue: any
    ) => {
      if (!cfg?.clientEnv) return undefined;
      if (typeof cfg.clientEnv === 'function') {
        return cfg.clientEnv(index, prevValue);
      }
      return cfg.clientEnv;
    };

    const resolveAuth = (
      cfg: ClientPlaygroundConfigOptions<any, any> | undefined,
      index: number,
      ctx: { baseAuth: any; clientEnv: any }
    ) => {
      if (!cfg?.clientAuth) return undefined;
      if (typeof cfg.clientAuth === 'function') {
        return cfg.clientAuth({ index, baseAuth: ctx.baseAuth, clientEnv: ctx.clientEnv });
      }
      return cfg.clientAuth;
    };

    const created: MockClient[] = [];
    let previousBaseEnv: any;
    let previousOverrideEnv: any;
    let previousFinalEnv: any;

    try {
      for (let i = 0; i < total; i++) {
        const canvasIndex = startIndex + i;
        let canvas = canvases[canvasIndex];
        let containerFromSetup: HTMLElement | undefined;

        if (hasDOM) {
          const baseDomSetup = normalizeDomSetupResult(baseOptions?.domSetup?.(i));
          const overrideDomSetup = normalizeDomSetupResult(options.domSetup?.(i));
          const mergedDom = {
            container: overrideDomSetup.container ?? baseDomSetup.container,
            canvas: overrideDomSetup.canvas ?? baseDomSetup.canvas,
          };
          containerFromSetup = mergedDom.container;
          if (mergedDom.canvas) {
            canvas = mergedDom.canvas;
          }
        }

        if (!canvas && hasDOM) {
          if (containerFromSetup) {
            canvas = ensureCanvasInContainer(containerFromSetup, `canvas${canvasIndex}`);
          } else {
            const fallback = document.getElementById(`canvas${canvasIndex}`) as HTMLCanvasElement | null;
            canvas = fallback ?? canvas;
          }
        }

        const baseEnvResult = resolveEnv(baseOptions, i, previousBaseEnv);
        if (baseEnvResult !== undefined) {
          previousBaseEnv = baseEnvResult;
        }

        const overrideEnvResult = resolveEnv(options, i, baseEnvResult ?? previousOverrideEnv ?? previousFinalEnv);
        if (overrideEnvResult !== undefined) {
          previousOverrideEnv = overrideEnvResult;
        }

        const finalEnvValue = overrideEnvResult ?? baseEnvResult ?? previousFinalEnv ?? undefined;

        const envDefaults: any = {};
        // Always use canvas from domSetup if provided (each client gets its own canvas)
        if (canvas) {
          envDefaults.canvas = canvas;
        }
        // Always use container from domSetup if provided (each client gets its own container for scoped UI mounting)
        if (containerFromSetup) {
          envDefaults.container = containerFromSetup;
        }
        if (typeof finalEnvValue !== 'object' || finalEnvValue === null || !(finalEnvValue as any).layout) {
          envDefaults.layout = 1;
        }
        if (typeof finalEnvValue !== 'object' || finalEnvValue === null || !(finalEnvValue as any).useDebug) {
          envDefaults.useDebug = i === 0;
        }

        const envConfig: any = { ...envDefaults };
        if (baseEnvResult && typeof baseEnvResult === 'object') {
          Object.assign(envConfig, baseEnvResult);
        }
        if (overrideEnvResult && typeof overrideEnvResult === 'object') {
          Object.assign(envConfig, overrideEnvResult);
        }

        const effectiveEnvForAuth = typeof finalEnvValue === 'object' && finalEnvValue !== null
          ? finalEnvValue
          : envConfig;

        previousFinalEnv = effectiveEnvForAuth;

        const clientEnvOptions = envConfig;

        // Counts every client this env created, as in createClientsWithStandardOptions.
        const defaultAuthIndex = createdClients.length;
        const client = await createClient(clientEnvOptions);
        // The client exists: its canvas is the env's next one.
        if (canvas) canvases[canvasIndex] = canvas;

        const canvasForCtx = canvases[canvasIndex];
        if (baseOptions?.onClientCreated) {
          await baseOptions.onClientCreated(client, { index: i, canvas: canvasForCtx });
        }
        if (options.onClientCreated && options.onClientCreated !== baseOptions?.onClientCreated) {
          await options.onClientCreated(client, { index: i, canvas: canvasForCtx });
        }

        const joinedHandlers = [baseOptions?.onJoinedArea, options.onJoinedArea].filter(Boolean) as ((client: MockClient, area: any) => void | Promise<void>)[];
        if (joinedHandlers.length) {
          client.on('joined-area', async (area: any) => {
            for (const handler of joinedHandlers) {
              await handler(client, area);
            }
          });
        }

        const defaultAuth: any = { id: `test_client_${defaultAuthIndex}`, username: `Player ${defaultAuthIndex}` };
    const baseAuth = resolveAuth(baseOptions, i, { baseAuth: defaultAuth, clientEnv: baseEnvResult ?? effectiveEnvForAuth }) ?? (baseOptions ? defaultAuth : undefined);
    const effectiveBaseAuth = baseAuth ?? defaultAuth;
    const authPayload = resolveAuth(options, i, { baseAuth: effectiveBaseAuth, clientEnv: effectiveEnvForAuth }) ?? effectiveBaseAuth;

        await client.authenticate(authPayload);
        // The instance's `joinGame` wins over the base's, like every other field.
        const join = options.joinGame ?? baseOptions?.joinGame;
        if (join) await joinClientToGame(client, join, { index: i, auth: authPayload });
        created.push(client);
      }
    } catch (err) {
      // A creation failed: a canvas this call made for a client that was never created leaves the page, unrecorded;
      // one the caller supplied stays where it is.
      for (const canvas of made) {
        if (!canvases.includes(canvas)) canvas.remove();
      }
      throw err;
    }

    return created;
  };

  const createClients = async (
    count: number,
    optionsArg?: CreateClientsOptions | ClientPlaygroundConfigOptions,
    basePlaygroundArg?: ClientPlaygroundConfigOptions
  ): Promise<MockClient[]> => {
    // The three-argument form is the playground form: `options` layers over `base` field by field
    // whatever keys either one carries — a layer holding only `joinGame` or `layout` is still a layer.
    // Only the two-argument form has to tell playground options from standard ones by their keys.
    if (basePlaygroundArg !== undefined || isClientPlaygroundConfigOptions(optionsArg)) {
      const instanceOptions = (optionsArg ?? basePlaygroundArg) as ClientPlaygroundConfigOptions<any, any>;
      const baseLayer = basePlaygroundArg && basePlaygroundArg !== instanceOptions ? basePlaygroundArg : undefined;
      return createClientsWithPlaygroundOptions(count, instanceOptions, baseLayer);
    }

    const options = (optionsArg ?? {}) as CreateClientsOptions;
    return createClientsWithStandardOptions(count, options);
  };

  const removeClient = async (clientOrId: MockClient | string): Promise<void> => {
    let clientId: string;
    let client: MockClient | undefined;

    if (typeof clientOrId === 'string') {
      clientId = clientOrId;
      client = clients[clientId];
    } else {
      client = clientOrId;
      // @ts-ignore
      clientId = client.id;
    }

    if (!client) {
      console.warn(`[removeClient] Client ${clientId} not found`);
      return;
    }
    // Areas and peers know a client by its publicId (`client.id`) — also when it
    // was looked up above by its privateId, the other key a gate registers it under.
    // @ts-ignore
    clientId = client.id;

    console.info(`[removeClient] Removing client ${clientId}`);

    // Handle peer cleanup for other clients. Each peer whose own end of the link is still open hears it end — once:
    // a peer that already ended its end (its `removePeer` close still on its way here) heard the end then, so it
    // hears nothing now, as production's peer does (its connection to this client is already gone).
    if (client.peerLookup) {
      Object.keys(client.peerLookup).forEach(peerId => {
        const info = client.peerLookup[peerId];
        if (info && info.client && info.client.peerLookup?.[clientId]) {
          const peerClient = info.client;
          // Notify peerClient that this client is gone
          if (peerClient.process?.systemArray) {
            peerClient.process.systemArray.forEach((s: any) => {
              s.onAnyPeerConnectionEnded?.(clientId);
            });
          }

          const sysName = info.system;
          if (sysName !== undefined && peerClient.process?.systemLookup && String(sysName) in peerClient.process.systemLookup) {
             const system = peerClient.process.systemLookup[String(sysName)];
             (system as any)?.onPeerSystemConnectionEnded?.(clientId);
          }

          if (peerClient.peerLookup) {
            delete peerClient.peerLookup[clientId];
          }
          (peerClient as any).handlePeerConnectionEnded?.(clientId);
        }
      });
      client.peerLookup = {};
    }

    // Trigger leave on all joined areas
    if (client.process && client.process.joinedAreasById) {
      const areaIds = Object.keys(client.process.joinedAreasById);
      for (const areaId of areaIds) {
        const area = areas[areaId];
        if (area && typeof area.handleClientLeave === 'function') {
          // Use Gotti core constant for disconnect reason if available, otherwise just a string
          await area.handleClientLeave(clientId, LEAVE_AREA_REASONS.REMOVED_BY_AREA); // 1000 is usually standard disconnect
        }
      }
    }

    // Stop client process
    client.process?.stopLoop();
    

    // Disconnect connector
    // @ts-expect-error -- accessing private member for mock/test env cleanup
    client.connector?.disconnect();

    // Remove from clients map under EVERY key it is registered by: with a gate,
    // authenticateWithPlayground registers a client under its privateId AND its
    // publicId, so deleting `clientId` alone left it listed in `env.clients`.
    for (const key of Object.keys(clients)) {
      if (clients[key] === client) delete clients[key];
    }
  };

  // auto-create clients if initializer options included configs
  const initClientsCfg: InitializeClientsConfig | Array<InitializeClientsConfig> | undefined = (opts as MockEnvironmentOptions).initClients as any;
  if (initClientsCfg) {
    const cfgs = Array.isArray(initClientsCfg) ? initClientsCfg : [initClientsCfg];
    for (const cfg of cfgs) {
      const count = cfg?.count ?? 1;
      await createClients(count, cfg);
    }
  }

  const clientSystems: ClientSystemType[] = [];
  const serverSystems: IServerSystem[] = [];

  // The env object is built first; the environment mixins then attach to it in order, each over the one
  // resolver (ticks first: later mixins build on `onTick`).
  const resolver = createProcessResolver({ areas, clients });
  // `pumpCarrySec` is this env's pump carry: only `pace` reads it, so it stays off the env.
  const { queries, pumpCarrySec } = createEnvironmentQueries(resolver, gameTimeMeters);
  const base = {
    clientSystems, serverSystems, createClient, createClients, removeClient, clients, areas, canvases: canvasesShared, assetManager,
    connectors: connectorIds, killConnector, addConnector, removeConnector,
    report: (...args: unknown[]) => console.info(...args),
    ...queries,
  };
  // `() => base.report` reads the live field: the playground reassigns `env.report` after boot.
  const withTicks = Object.assign(base, createTickQueries(resolver, () => base.report));
  // Pacing pumps through the env's own `pump`/`gameTimeMs`, so it attaches after the base queries exist.
  const withPacing = Object.assign(withTicks, createPacingQueries(resolver, withTicks, pumpCarrySec));
  // The game-clock waits read the env's own `gameTimeMs`; `holdFor` judges on the resolved process's ticks.
  const withWaits = Object.assign(withPacing, createWaitQueries(resolver, withPacing));
  // The rendering gate samples a client's canvas inside its ticks, through the env's own `onTick`.
  const env: MockEnvironment = Object.assign(withWaits, createRenderQueries(resolver, withWaits));

  // Register as the active test environment (singleton), together with the way
  // to end THIS env when it is torn down (see teardownEnv).
  _setActiveEnv(env, () => {
    const errors: unknown[] = [];
    /** Stops `process`'s loop if it still runs; what its systems' onStop throws is collected, not let stop the rest. */
    const stopLoopOf = (process: { startedLoop?: boolean; stopLoop?: () => void } | undefined) => {
      // Only a loop still running: a host that stopped them first (mountPlayground's dispose) must not have every
      // system hear onStop twice — core's stopLoop runs them again on every call.
      if (!process?.startedLoop) return;
      try {
        process.stopLoop?.();
      } catch (err) {
        errors.push(err);
      }
    };
    createdClients.forEach((c) => {
      // The real Client flag its reconnect driver checks on entry AND after every
      // backoff wait — so an in-flight episode ends instead of outliving the env.
      (c as any).forceClosed = true;
      // Mirrors BaseClientConnector.disconnect(): drops onConnectionLost, so a
      // later connector death on a dead env can't start a new episode either.
      (c as any).connector?.disconnect?.();
      stopLoopOf(c.process as any);
      closedEndpoints.add(c);
    });
    for (const area of Object.values(areaServers)) {
      stopLoopOf(area.gameProcess as any);
      closedEndpoints.add(area);
    }
    for (const { timer } of pendingWhenEmptyDestroys.values()) clearTimeout(timer);
    pendingWhenEmptyDestroys.clear();
    return errors;
  });

  return env;
};

// ─── Active environment singleton ────────────────────────────────────────────
// Used by test helpers (waitFor, waitForFrames, etc.) so they don't need
// the env passed explicitly. initializer() auto-registers; teardown nulls it.

let _activeEnv: MockEnvironment | null = null;
/** Ends the active env (initializer's), returning what its systems' onStop threw. */
let _closeActiveEnv: (() => unknown[]) | null = null;

/** @internal — called by initializer() */
function _setActiveEnv(env: MockEnvironment | null, close?: () => unknown[]): void {
  _activeEnv = env;
  _closeActiveEnv = close ?? null;
}

/**
 * Returns the currently active MockEnvironment set by `initializer()`.
 * Throws if no environment is active (not yet created, or already torn down).
 */
export function getActiveEnv(): MockEnvironment {
  if (!_activeEnv) {
    throw new Error(
      '[test-utils] No active test environment. ' +
      'Call initializer() before using test helpers like waitFor / waitForFrames. ' +
      'If you already called initializer(), the environment may have been torn down — ' +
      'check that your test setup runs before assertions.'
    );
  }
  return _activeEnv;
}

/**
 * Tear down the active environment singleton.
 * Call this in afterEach / afterAll to ensure stale references error loudly.
 *
 * It ENDS the outgoing env, and that is not optional hygiene — a torn-down env
 * whose parts kept running would work on, unseen, inside the tests after it:
 * - its clients' reconnect episodes end: the patched client prototype resolves
 *   `currentClientPrototypeState` — a MODULE GLOBAL — at call time, so a client
 *   whose real reconnect driver is still in its backoff loop would, on its next
 *   attempt, seat itself on the NEXT env's connectors and rejoin the NEXT env's
 *   areas (firing that test's hooks, from a client it never created);
 * - every area and client game loop still running stops (onStop runs once, for
 *   a loop not already stopped by its host): a loop re-arms with the global
 *   `setTimeout`/`setImmediate`, so a later test that installs fake timers
 *   would capture it and spin it through its advance;
 * - its transport closes: nothing is delivered from or to its areas and clients
 *   any more, queued or sent after (see `closedEndpoints`).
 *
 * What the systems' onStop threw is thrown once all of that is done: one error
 * as itself, several as an AggregateError. The env is torn down either way.
 */
export function teardownEnv(): void {
  const close = _closeActiveEnv;
  const errors = close?.() ?? [];
  _activeEnv = null;
  _closeActiveEnv = null;
  if (errors.length === 1) throw errors[0];
  if (errors.length > 1) {
    throw new AggregateError(errors, `[test-utils] ${errors.length} systems threw in onStop; the environment is torn down.`);
  }
}
